import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowRight, Check, Download, Network, Pencil, Plus, Route, Swords, Trash2 } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { ApiError, api } from "../api/client";
import type {
  EnemyType,
  OdbKind,
  Exercise,
  ExerciseParameters,
  Force,
  Level,
  MissionOption,
  NamedPlace,
  OdbSummary,
  OrientationOut,
  WeatherCustom,
  ZoneLetter,
} from "../api/types";
import { attentionSelector, designate, designateSoon, flashAttention, REVEAL_EVENT, REVEAL_MS } from "../components/attention";
import { awaitingFor } from "../components/awaiting";
import { Callout } from "../components/Callout";
import { ChiefOrderCallout } from "./received/ChiefOrderNotice";
import { CampOutline } from "../components/CampOutline";
import { Checkbox } from "../components/Checkbox";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DeletionContents } from "../app/home/DeletionContents";
import { ChoiceCard } from "../components/panel";
import { Button, ButtonLink, Field, FloatingPanel, IconButton, IconLink, inputClass, Segmented } from "../components/ui";
import { StepMark } from "../components/StepMark";
import { VIEW_META } from "../components/viewMeta";
import { genericEnemyLabel, isNeutral } from "../enemyDisplay";
import { HelpButton } from "../help/HelpButton";
import { COMPANY_DEFAULT_MISSION, companyCard } from "../company";
import { unitDesignation } from "../i18n/designation";
import { k, lang, t, tServer } from "../i18n/i18n";
import { useTrialExport } from "../licence/trial";
import { missionName, term } from "../i18n/vocabulary";
import { specialtyLabel } from "../missions";
import { useUi } from "../state/ui";
import { accountsItem, designateDossierReadOnly, readOnly } from "../sharing/access";
import { useUnsavedDraft } from "../state/unsavedDrafts";
import { Motion, Presence } from "../components/presence";
import { centacDraftName, deducedRotation, isRotationCode, nameLeavingCentac, rotationError, rotationNameNote } from "../centacRotation";
import { CentacRotationField } from "./CentacRotationField";
import { CENTAC_ROTATION_FIELD, OPEN_DRONES_SETTINGS, WEATHER_FIELD } from "./missionDataTarget";
import { placeableCount } from "../map/units/eniUnits";
import { useDeployment } from "../map/units/useDeployment";
import { orderActions, ordersNote, refreshOrders } from "../map/orderSync";
import { CompositionSummary } from "./composition/CompositionSummary";
import {
  availablePostures,
  chooseMission,
  choosePosture,
  defaultMissionFor,
  draftPosture,
  type DossierPosture,
  missionsFor,
  POSTURE_LABELS,
} from "./missionPosture";
import { formatDtg, isZoneLetter, zoneLabel, ZONES_WEST_TO_EAST } from "../map/dtg";
import { Select } from "../components/Select";
import { ENI_KIND_BY_LEVEL, eniKindFor, eniOdbsFor, isGenericEniOdb } from "./orbats/eni";
import { functionLabel } from "./orbats/app6";
import { useOrbatsTarget } from "./orbats/orbatsTarget";
import { customIdOf, customValue, useCustomEnemyTarget } from "./customEnemy/customEnemyTarget";
import { TrainingState } from "./customEnemy/TrainingState";
import { useApp6Palette } from "./orbats/useApp6Palette";
import { useZoneDraw } from "../map/zones/zoneDraw";
import { usePendingZone } from "../map/zones/ZoneWaitCallout";
import { refreshAfterDossiersRemoved, useZoneList } from "../map/zones/useZones";
import { showZoneWait } from "../map/zones/zoneAttention";
import { FREE_TERRAIN, zoneChoices, zoneOptionGroup } from "../map/zones/zoneChoices";
import { useFreeTerrainOffer } from "../map/zones/zonesOpen";
import { proposedTimeZone, unchosenTimeZone } from "../map/zones/zoneState";
import {
  afterSave,
  derivedSite,
  layoutOf,
  type MapKind,
  mapProfileShown,
  pendingTerrain,
  pickSite,
  placesTerrain,
  type SaveIntent,
  type SaveKind,
  saveKind,
  type Site,
  weatherWaits,
} from "./missionDataFlow";
import {
  type Conflicts,
  type Draft,
  type DraftState,
  deploymentParamsOf,
  draftKey as draftKeyOf,
  draftReady,
  type FieldKey,
  freshDraftState,
  fromExercise,
  isModified,
  loadDraft,
  modifiedWithoutChoice,
  openConflicts,
  rebaseDraft,
  withDefaultForce,
  withForceEnemy,
  withResolvedMission,
  removeStoredDraft,
  sameValue,
  takeServerFields,
  withPlace,
  withSeasonalWeather,
  writeStoredDraft,
} from "./missionDraft";
import { onRadioGroupKeyDown, rovingTabIndex } from "./radioGroupKeys";
import { EnemyProfileField } from "./EnemyProfileField";
import { DronesField, hasChosen } from "./DronesField";
import { profileName } from "./enemyProfile";
import { FoldSection, OptionHeading, scrollFoldIntoView, StepsRail } from "./MissionFolds";
import { type WeatherPending, WeatherField } from "./WeatherField";
import { PLACE_FIELD, PlaceField, type PlaceZones } from "./PlaceField";
import {
  awaitedFields,
  firstOpenStep,
  type Fold,
  missingLine,
  missionSteps,
  nextLine,
  primaryMode,
  railState,
  type Step,
  type StepField,
  type StepKey,
  stepAfter,
  stepBadge,
  stepMarkState,
  type StepsPlace,
  stepsProgressLabel,
  summaryLine,
} from "./missionSteps";
import { designateDrones } from "./openMissionData";
import { refreshWeatherViews } from "./manoeuvre/TerrainWeather";
import { weatherKey, weatherState } from "../weather";

/** Libellé du bouton principal : il dit ce qui suit (`saveKind`). */
const SAVE_LABEL: Record<SaveKind, string> = {
  create: k("Créer le dossier"),
  "create-place": k("Créer et placer le terrain"),
  save: k("Enregistrer"),
  "save-place": k("Enregistrer et placer le terrain"),
  place: k("Placer le terrain"),
};

/** Délai au-delà duquel une demande d'enregistrement n'est plus traitée (vue ouverte plus tard). */
const SAVE_REQUEST_TTL_MS = 3000;

const LEVELS: { value: Level; label: string; hint: string }[] = [
  // Compagnie ou escadron (docs/21) : le libellé suit l'arme du S/GTIA choisi (`companyCard`).
  { value: "COMPAGNIE", label: k("Compagnie / Escadron"), hint: k("OPO du S/GTIA aux sections ou pelotons") },
  { value: "REGIMENT", label: k("Régiment"), hint: k("OPO du GTIA aux S/GTIA") },
  { value: "BRIGADE", label: k("Brigade"), hint: k("OPO de la brigade aux GTIA") },
  { value: "DIVISION", label: k("Division"), hint: k("OPO de la DIV aux brigades") },
  // Corps d'armée (docs/08 §23-24) : terrain libre seulement ; ordre au format du STANAG 2014, décliné aux divisions.
  { value: "CORPS", label: k("Corps d'armée (LCC)"), hint: k("OPO du corps aux divisions, en terrain libre") },
  { value: "CENTAC", label: k("CENTAC"), hint: k("OPO 1 à 4, WINGO, FRAGO") },
];
/** Corps d'armée : aucun camp n'est assez grand pour son fuseau (docs/08 §23). */
const CORPS_FREE_ONLY = k("Au niveau corps d'armée, aucun camp n'est assez grand pour un fuseau de 300 km : la manœuvre se construit sur un terrain libre.");

const FORCE_LABEL: Record<Level, string> = {
  COMPAGNIE: k("S/GTIA commandé"),
  REGIMENT: k("GTIA commandé"),
  BRIGADE: k("Brigade"),
  DIVISION: k("Division"),
  CORPS: k("Corps d'armée"),
  CENTAC: k("GTIA"),
};

const ENEMIES: EnemyType[] = ["Mercure", "Titan", "Titane", "Tantale", "Deimos"];
/** Valeur d'une option « Ennemi de référence (…) » de la liste des ordres de bataille ennemis. */
const REFERENCE = "reference:";
/** Nature d'ordre de bataille ami commandée à chaque niveau. */
// Compagnie : le S/GTIA se choisit dans un GTIA (docs/21 §2), créé dans l'éditeur comme au niveau régiment.
const AMI_KIND_BY_LEVEL: Record<Exclude<Level, "CENTAC">, OdbKind> = {
  COMPAGNIE: "gtia",
  REGIMENT: "gtia",
  BRIGADE: "brigade",
  DIVISION: "division",
  CORPS: "corps",
};

/** Titre d'un groupe de S/GTIA du catalogue (« 11BP · 1erRCP » : brigade, puis GTIA). */
function companyGroupLabel(group: string): string {
  const [brigade, gtia] = group.split(" · ");
  return gtia ? t("Brigade {brigade} · GTIA {gtia}", { brigade: brigade ?? "", gtia }) : t("GTIA {gtia}", { gtia: group });
}

const CENTAC_MAP = "mailly-le-camp";
/** Cartes de choix du lieu de l'exercice (disposition « cards »), dans l'ordre du groupe radio. */
const SITES = ["camp", "free"] as const;
const USER_GROUP = "\u0000vos-odb"; // groupe des forces construites par l'utilisateur
const USER_SUPPORT_GROUP = "\u0000vos-odb-appuis"; // les mêmes, hors manœuvre (bataillons d'appui et de soutien)

/** Avis de l'aperçu d'un brouillon modifié (`GET /deployment?base=`, registre K19 ; textes de
 *  generex_api/placement_state.py) : ce que l'enregistrement effacerait (registre K20). */
const CORRIDOR_CLEARED = "Fuseau personnalisé effacé : niveau, mission ou famille de gabarit changés";
const CORRIDOR_CLEARED_CAMP = "Fuseau personnalisé effacé : autre camp";
const MISSIONS_CLEARED = "Missions choisies effacées : niveau, ordre de bataille ou mission changés";
/** Sens de la manœuvre (docs/14 §2.14) effacé : autre camp, terrain libre ou CENTAC. */
const ORIENTATION_CLEARED = "Sens de la manœuvre effacé : autre terrain ou rotation CENTAC";
/** Notes du déploiement sur l'ordre de bataille ennemi du dossier (textes de generex_engine/placement.py et
 *  generex_api/deployment.py) : montrées sous son choix, traduites par `tServer`. */
const ENI_NOTES = [
  "ODB ennemi incomplet : unité de référence conservée",
  "ODB ennemi incomplet : emplacement laissé vide pour ne pas doubler une unité de l'ODB",
  "Ordre de bataille ennemi introuvable : ennemi de référence rétabli",
  "Ordre de bataille ennemi d'un autre niveau : ennemi de référence rétabli",
  // Mêmes notes face à l'ennemi générique (force non française, generex_api/enemy_display.py).
  "ODB ennemi incomplet : unité de l'ennemi générique conservée",
  "Ordre de bataille ennemi introuvable : ennemi générique rétabli",
  "Ordre de bataille ennemi d'un autre niveau : ennemi générique rétabli",
];
/** Avertissement « sens de la manœuvre non appliqué » de l'aperçu d'un brouillon (docs/14 §2.14), sinon false. */
function orientationLost(block: OrientationOut | null | undefined): string | false {
  if (!block || block.requested_deg === null || block.applied) return false;
  const deg = Math.round(block.requested_deg) % 360;
  return t("Le sens de la manœuvre ({cap}) ne tient pas dans le camp pour cette mission : le sens par défaut sera utilisé.", {
    cap: `${String(deg).padStart(3, "0")}°`,
  });
}

/** Avis du serveur quand le niveau change et que l'ordre de bataille ennemi n'y fait plus face (routes/exercises.py). */
const ENI_ODB_CLEARED = "Ordre de bataille ennemi retiré : il ne fait pas face au nouveau niveau de l'ordre";
/** Avis du serveur au passage en CENTAC d'un dossier à météo choisie (generex_api/weather_settle.py) : le même mot ici,
 *  dès le changement de niveau dans le brouillon. */
const WEATHER_CENTAC = "Météo choisie retirée : en CENTAC, la météo se rédige dans l'ordre de la rotation.";

/** Axe de manœuvre CENTAC : direction de l'ennemi depuis la base de départ (le compas de l'iPad). */
const AXES: { deg: number; label: string }[] = [
  { deg: 0, label: k("Nord — dispositif de référence") },
  { deg: 45, label: k("Nord-est") },
  { deg: 90, label: k("Est") },
  { deg: 135, label: k("Sud-est") },
  { deg: 180, label: k("Sud") },
  { deg: 225, label: k("Sud-ouest") },
  { deg: 270, label: k("Ouest") },
  { deg: 315, label: k("Nord-ouest") },
];

// Valeurs enregistrées dans le dossier (en français) ; libellés traduits à l'affichage.
const POPULATIONS = [
  { value: "Amicale", label: k("Amicale") },
  { value: "Neutre", label: k("Neutre") },
  { value: "Hostile", label: k("Hostile") },
] as const;
const YES_NO = [
  { value: "Oui", label: k("Oui") },
  { value: "Non", label: k("Non") },
] as const;

/** Options d'un choix segmenté, libellés dans la langue de l'interface. */
function translated<T extends string>(options: readonly { value: T; label: string }[]) {
  return options.map((option) => ({ value: option.value, label: t(option.label) }));
}

function groupBy<T>(items: T[], key: (item: T) => string): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    groups.set(k, [...(groups.get(k) ?? []), item]);
  }
  return [...groups.entries()];
}

/** Champ modifié aussi dans le dossier enregistré depuis la saisie (report sur une version plus récente) : la saisie
 *  est gardée, la valeur enregistrée reste à un clic (avec ce qui va avec elle : la mission d'une autre force, la
 *  force et l'ODB ennemi d'un autre niveau). */
function ConflictHint({ show, onTake }: { show: boolean; onTake: () => void }) {
  return (
    <Presence show={show}>
      <Motion origin="fade" className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-meta leading-snug text-warn">
        <span>{t("Modifié aussi dans le dossier enregistré : votre valeur est gardée.")}</span>
        <Button variant="link" onClick={onTake}>
          {t("Reprendre la valeur enregistrée")}
        </Button>
      </Motion>
    </Presence>
  );
}

/** Ordre de bataille ennemi du dossier (docs/14 §3.5, §7.9) : l'ennemi de référence, ou un ordre de bataille ennemi
 *  de la licence qui fait face au niveau de l'ordre. Sous la liste, ce que la manœuvre en a fait (aperçu du
 *  brouillon ou dispositif du dossier) : emplacements restés à l'unité de référence, unités à glisser sur la carte.
 *  Force non française (`neutral`, registre B39) : l'ennemi générique par défaut, ou un ordre de bataille ennemi
 *  générique ; un ordre de bataille ennemi d'une faction resté d'avant (`legacy`) est signalé, jamais nommé. */
function EniOdbField({
  level,
  value,
  choices,
  chosen,
  loaded,
  enemy,
  neutral,
  legacy,
  onChange,
  onReference,
  onCreate,
  onEdit,
  conflict,
}: {
  level: Level;
  value: string | null;
  choices: OdbSummary[];
  chosen: OdbSummary | null;
  /** Liste des ordres de bataille ennemis reçue (avant : aucun n'est dit introuvable). */
  loaded: boolean;
  enemy: EnemyType | null;
  /** Ennemi générique (force non française) : aucun ennemi de référence nommé. */
  neutral: boolean;
  /** Ordre de bataille ennemi d'une faction choisi avant, face à l'ennemi générique : à remplacer. */
  legacy: boolean;
  onChange: (code: string | null) => void;
  /** Ennemi de référence choisi dans la liste (aucun ordre de bataille ennemi de l'utilisateur) : il devient l'ennemi
   *  du dossier, comme dans le champ « Ennemi » (demande d'Alexis du 13/10/2026). */
  onReference: (enemy: EnemyType) => void;
  /** « Créer un ordre de bataille ennemi » : écran des ordres de bataille, dialogue prérempli (niveau de l'ordre). */
  onCreate: () => void;
  /** « Modifier » l'ordre de bataille ennemi choisi (null : aucun, ou enregistré dans le dossier sans éditeur). */
  onEdit: (() => void) | null;
  /** Signalement « modifié aussi dans le dossier enregistré », sous la liste. */
  conflict?: ReactNode;
}) {
  const { data: deployment, isPlaceholderData } = useDeployment();
  const pendingZone = usePendingZone();
  const placed = !isPlaceholderData && value && deployment?.eni_odb?.code === value ? deployment.eni_odb : null;
  const notes = !isPlaceholderData && value ? (deployment?.notes ?? []).filter((n) => ENI_NOTES.includes(n)) : [];
  const unfilled = placed?.unfilled.length ?? 0;
  const waiting = placeableCount(placed);
  const kind = eniKindFor(level);
  return (
    <div data-attention="dossier-eni-odb" className="space-y-1.5 rounded-lg">
      <Field
        label={t("Ordre de bataille ennemi")}
        hint={
          loaded && choices.length === 0 && !value
            ? neutral
              ? kind === "eni_corps"
                ? t("Aucun ordre de bataille ennemi de corps d'armée : créez-en un pour détailler l'ennemi générique.")
                : kind === "eni_brig"
                ? t("Aucun ordre de bataille ennemi de brigade : créez-en un pour détailler l'ennemi générique.")
                : kind === "eni_div"
                  ? t("Aucun ordre de bataille ennemi de division : créez-en un pour détailler l'ennemi générique.")
                  : t("Aucun ordre de bataille ennemi de bataillon : créez-en un pour détailler l'ennemi générique.")
              : kind === "eni_corps"
                ? t("Aucun ordre de bataille ennemi de corps d'armée : créez-en un pour remplacer l'ennemi de référence.")
                : kind === "eni_brig"
                ? t("Aucun ordre de bataille ennemi de brigade : créez-en un pour remplacer l'ennemi de référence.")
                : kind === "eni_div"
                  ? t("Aucun ordre de bataille ennemi de division : créez-en un pour remplacer l'ennemi de référence.")
                  : t("Aucun ordre de bataille ennemi de bataillon : créez-en un pour remplacer l'ennemi de référence.")
            : undefined
        }
      >
        <Select
          className={inputClass}
          value={value ?? (neutral ? "" : `${REFERENCE}${enemy ?? "Titan"}`)}
          onChange={(e) => {
            const picked = e.target.value;
            if (picked.startsWith(REFERENCE)) onReference(picked.slice(REFERENCE.length) as EnemyType);
            else onChange(picked || null);
          }}
        >
          {neutral ? (
            <option value="">{t("Ennemi générique (par défaut)")}</option>
          ) : (
            // Les ennemis de référence, tous proposés : choisir l'un d'eux fixe aussi le champ « Ennemi ».
            ENEMIES.map((en) => (
              <option key={en} value={`${REFERENCE}${en}`}>
                {t("Ennemi de référence ({enemy})", { enemy: en })}
              </option>
            ))
          )}
          {(choices.length > 0 || (loaded && value && !chosen)) && (
            <optgroup label={neutral ? t("Vos ordres de bataille ennemis génériques") : t("Vos ordres de bataille ennemis")}>
              {choices.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.faction_name && !neutral ? `${unitDesignation(o.name)} — ${o.faction_name}` : unitDesignation(o.name)}
                </option>
              ))}
              {/* Ordre de bataille d'une faction resté d'avant : jamais nommé (son nom peut citer un code de faction). */}
              {loaded && value && !chosen && (
                <option value={value}>{legacy ? t("Ordre de bataille ennemi actuel (non générique)") : t("{code} (introuvable)", { code: value })}</option>
              )}
            </optgroup>
          )}
        </Select>
      </Field>
      {conflict}
      <Presence show={legacy}>
        <Motion origin="fade">
          <Callout tone="warn">
            {t("Cet ordre de bataille ennemi ne convient pas à une force construite : choisissez un ordre de bataille ennemi générique, ou l'ennemi générique par défaut.")}
          </Callout>
        </Motion>
      </Presence>
      <Presence show={unfilled > 0 || notes.length > 0 || waiting > 0}>
        <Motion origin="fade">
          <Callout tone={unfilled > 0 || notes.length > 0 ? "warn" : "info"}>
            <ul className="space-y-0.5">
              {unfilled > 0 && (
                <li>
                  {t("{n} emplacement(s) de la manœuvre sans unité de cet ordre de bataille : complétez-le, ou changez le rôle de ses unités.", {
                    n: unfilled,
                  })}
                </li>
              )}
              {notes.map((note) => (
                <li key={note}>{tServer(note)}</li>
              ))}
              {waiting > 0 &&
                (pendingZone ? (
                  // Zone libre dont le terrain se prépare : la pose attend la zone (le serveur la refuse).
                  <li>{t("{n} unité(s) non placée(s) : à glisser sur la carte quand le terrain sera prêt.", { n: waiting })}</li>
                ) : (
                  <li>{t("{n} unité(s) non placée(s) : glissez-les sur la carte depuis la liste des unités (vue Manœuvre).", { n: waiting })}</li>
                ))}
            </ul>
          </Callout>
        </Motion>
      </Presence>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-0.5">
        <Button size="sm" variant="secondary" onClick={onCreate}>
          <Swords size={14} /> {t("Créer un ordre de bataille ennemi")}
        </Button>
        {onEdit && chosen && (
          <Button variant="link" onClick={onEdit}>
            <Pencil size={14} /> {t("Modifier « {name} »", { name: unitDesignation(chosen.name) })}
          </Button>
        )}
      </div>
      {/* Composition propre au dossier côté ennemi et correspondance avec l'OPO (docs/18 §6.6). */}
      <CompositionSummary side="ENI" />
    </div>
  );
}

/** « Données initiales » : les paramètres de l'exercice (ex-onglet Données d'entrée de l'iPad).
 *  Le dispositif affiché sur la carte suit le brouillon en cours, avant même l'enregistrement. */
export function MissionSummary() {
  const trialExport = useTrialExport();
  const queryClient = useQueryClient();
  const exerciseId = useUi((s) => s.exerciseId);
  const setExercise = useUi((s) => s.setExercise);
  const setMapSlug = useUi((s) => s.setMapSlug);
  const setDeploymentParams = useUi((s) => s.setDeploymentParams);
  const setWeatherKey = useUi((s) => s.setWeatherKey);
  const saveRequest = useUi((s) => s.saveRequest);
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const setDraftLanguage = useUi((s) => s.setDraftLanguage);
  const nameFocus = useUi((s) => s.nameFocus);
  const welcome = useUi((s) => s.welcome);
  const requestZoneStart = useZoneDraw((s) => s.requestStart);
  const terrainFor = useZoneDraw((s) => s.terrainFor);
  const setTerrainFor = useZoneDraw((s) => s.setTerrainFor);
  const frameDossier = useUi((s) => s.frameDossier);

  const { data: maps = [], isSuccess: mapsLoaded } = useQuery({ queryKey: ["maps"], queryFn: api.maps });
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const current = exercises.find((e) => e.id === exerciseId);

  // État de saisie : le brouillon, le dossier dont il part (`base`, et sa version), les champs modifiés des deux
  // côtés et l'avis « le dossier a changé » (`missionDraft.ts`).
  const [initial] = useState<DraftState>(() => loadDraft(current));
  const [draft, setDraft] = useState<Draft>(initial.draft);
  const [base, setBase] = useState<Draft>(initial.base);
  const [baseVersion, setBaseVersion] = useState(initial.baseVersion);
  const [conflicts, setConflicts] = useState<Conflicts>(initial.conflicts);
  const [rebased, setRebased] = useState(initial.rebased);
  const adopt = (state: DraftState) => {
    setDraft(state.draft);
    setBase(state.base);
    setBaseVersion(state.baseVersion);
    setConflicts(state.conflicts);
    setRebased(state.rebased);
  };
  // Dossier auquel appartient le brouillon affiché : au changement de dossier (ou à l'arrivée de la liste des
  // dossiers), le brouillon précédent ne doit pas être enregistré sous la clé du nouveau.
  const owner = current?.id ?? "new";
  const [draftOwner, setDraftOwner] = useState(owner);
  const [error, setError] = useState<string | null>(null);
  // Erreur de saisie refusée par le serveur (409, 422 : numéro de rotation déjà pris, valeur invalide) : à corriger
  // dans le formulaire, pas une anomalie à signaler.
  const [inputError, setInputError] = useState(false);
  // Carte de choix du lieu cochée par l'utilisateur (« Camp de manœuvre » ou « Terrain libre ») ; null : déduite du
  // brouillon (`derivedSite`). Jamais dans le brouillon : ni dans la comparaison « modifié », ni dans le stockage.
  const [siteChoice, setSiteChoice] = useState<Site | null>(null);
  // Mission à reprendre après un changement de force fait ici : la même si la nouvelle force la propose, sinon la
  // première (une mission enregistrée introuvable, elle, n'est jamais remplacée d'office).
  const preferredMission = useRef<string | null>(null);
  useEffect(() => {
    if (draftOwner === owner && current) {
      if (baseVersion === current.version) return; // déjà sur cette version (enregistrement fait ici)
      if (!sameValue(draft, base)) {
        // Le dossier a changé sous une saisie en cours (autre poste, carte, faction d'un ODB ennemi, refus à
        // l'enregistrement) : la saisie est reportée champ par champ, jamais jetée.
        const latest = fromExercise(current);
        const next = rebaseDraft(draft, base, latest, conflicts);
        adopt({ ...next, base: latest, baseVersion: current.version, rebased: rebased || !sameValue(next.draft, latest) });
        setError(null);
        return;
      }
    }
    adopt(loadDraft(current));
    setDraftOwner(owner);
    setError(null);
    setSiteChoice(null);
    preferredMission.current = null;
  }, [current?.id, current?.version]);

  // La carte nomme les lignes selon la langue du brouillon (PL0 à PL3 en anglais), comme elle suit son dispositif.
  useEffect(() => {
    setDraftLanguage(draft.language);
  }, [draft.language, setDraftLanguage]);

  const level = draft.parameters.level ?? "REGIMENT";
  const isCentac = level === "CENTAC";

  // Dossier ouvert (`base=`, comptes nominatifs) : son ODB enregistré figure au sélecteur même s'il est privé à son
  // propriétaire ; l'éditeur d'un dossier partagé y voit la valeur actuelle et change de GTIA dans le même ODB.
  const baseId = current && accountsItem(current) ? current.id : null;
  const forcesQuery = useQuery({
    queryKey: ["forces", level, baseId],
    queryFn: () => api.forces(level, baseId),
  });
  const forces = useMemo(() => forcesQuery.data ?? [], [forcesQuery.data]);
  const app6Palette = useApp6Palette(forces.some((f) => f.app6 || f.function));
  // Force choisie, et ennemi qu'elle affronte (registre B39) : une force non française affronte l'ennemi générique.
  // Le serveur le dit par `reference_enemy` (force de la liste, sinon dossier enregistré) ; le client n'applique
  // aucune règle à lui.
  const chosenForce = forces.find((f) => f.ref === draft.parameters.odb) ?? null;
  const neutral = !isCentac && (chosenForce ? isNeutral(chosenForce.reference_enemy) : isNeutral(current?.reference_enemy));
  // Ordres de bataille ennemis de la licence (docs/14 §3.5) : ceux qui font face au niveau de l'ordre.
  const { data: eniListing } = useQuery({
    queryKey: ["odbs", "ENI", baseId],
    queryFn: () => api.odbs("ENI", baseId),
    enabled: !isCentac,
  });
  const eniChoices = useMemo(() => eniOdbsFor(eniListing?.user ?? [], level, neutral), [eniListing, level, neutral]);
  const eniOdb = draft.parameters.eni_odb ?? null;
  const eniChosen = eniChoices.find((o) => o.code === eniOdb) ?? null;
  // Face à l'ennemi générique, ordre de bataille ennemi d'une faction resté d'avant (dossier hérité, ou force changée
  // dans le brouillon) : toléré par le serveur tant qu'il ne change pas, signalé et son champ désigné.
  const eniLegacy =
    neutral &&
    Boolean(eniOdb) &&
    eniOdbsFor(eniListing?.user ?? [], level).some((o) => o.code === eniOdb && !isGenericEniOdb(o));
  // ODB ennemi d'une faction : l'ennemi du dossier est le sien (champ figé) ; ODB ennemi générique (sans faction) :
  // l'ennemi reste au choix, il donne les unités de référence des emplacements que l'ODB ne pourvoit pas.
  const eniFaction = Boolean(eniOdb) && (eniChosen ? Boolean(eniChosen.faction_name) : !eniListing);
  const openEniOrbats = useOrbatsTarget((s) => s.open);
  const pendingUse = useOrbatsTarget((s) => s.use);
  // ENI personnalisés de la licence (registre A135), proposés dans le champ « Ennemi » après ceux de référence.
  const customListing = useQuery({ queryKey: ["custom-enemies"], queryFn: api.customEnemies });
  const customEnemies = customListing.data?.items ?? [];
  const pendingCustom = useCustomEnemyTarget((s) => s.use);
  const missionsQuery = useQuery({
    queryKey: ["missions", level, draft.parameters.odb, baseId],
    queryFn: () => api.missions(level, draft.parameters.odb, baseId),
    enabled: !isCentac && Boolean(draft.parameters.odb),
  });
  const missions = useMemo(() => missionsQuery.data ?? [], [missionsQuery.data]);

  // Dossier d'où partent les liens vers l'écran des ordres de bataille (« Employer dans le dossier » au retour) ;
  // face à l'ennemi générique, l'éditeur y cache les ennemis des unités françaises.
  const orbatsOrigin = (field: "odb" | "eni_odb") => ({
    exerciseId: current?.id ?? null,
    exerciseName: current?.name ?? (draft.name.trim() || null),
    field,
    level: (level === "CENTAC" ? "REGIMENT" : level) as "REGIMENT" | "BRIGADE" | "DIVISION" | "CORPS" | "COMPAGNIE",
    referenceEnemy: !neutral,
  });

  // ENI personnalisé du brouillon (registre A135) : son résumé (état du modèle, nom de code), choix et ouverture de
  // la vue « ENI personnalisés » avec ce dossier pour origine (« Employer dans le dossier » au retour).
  const chosenCustom = !isCentac && !neutral ? (draft.parameters.custom_enemy ?? null) : null;
  const chosenCustomSummary = chosenCustom ? customEnemies.find((c) => c.id === chosenCustom) : undefined;
  const chooseEnemy = (value: string) => {
    const id = customIdOf(value);
    const custom = id ? customEnemies.find((c) => c.id === id) : undefined;
    setDraft((d) => ({
      ...d,
      parameters: custom
        ? // Ennemi porteur de l'ENI (faction des unités) ; un ODB ennemi d'une faction garde la sienne.
          { ...d.parameters, custom_enemy: custom.id, enemy_type: eniFaction ? d.parameters.enemy_type : custom.base_enemy }
        : { ...d.parameters, custom_enemy: null, enemy_type: (value || null) as EnemyType | null },
    }));
  };
  const openCustomEnemies = (enemyId: string | null, create = false) => {
    useCustomEnemyTarget.getState().open(enemyId, {
      create,
      origin: { exerciseId: current?.id ?? null, exerciseName: current?.name ?? (draft.name.trim() || null) },
    });
    setView("enemies");
  };

  const setParam = <K extends keyof ExerciseParameters>(key: K, value: ExerciseParameters[K]) =>
    setDraft((d) => ({ ...d, parameters: { ...d.parameters, [key]: value } }));
  // Météo des Données initiales : scénario (« auto », « custom ») et choix posés ensemble.
  const setWeather = useCallback(
    (scenario: string, custom: WeatherCustom | null) =>
      setDraft((d) =>
        d.parameters.weather_scenario === scenario && sameValue(d.parameters.weather_custom ?? null, custom)
          ? d
          : { ...d, parameters: { ...d.parameters, weather_scenario: scenario, weather_custom: custom } },
      ),
    [setDraft],
  );
  // Nom d'un dossier CENTAC (registre A122), comme le serveur le tire du numéro ; sous le champ, l'ancien nom.
  const centacName = (code: string | null | undefined) => centacDraftName(code, current);
  const setRotation = (value: string | null) =>
    setDraft((d) => ({ ...d, name: centacName(value), parameters: { ...d.parameters, centac_rotation: value } }));
  const rotationNote = rotationNameNote(current);
  const server = useMemo(() => fromExercise(current), [current]);
  const dirty = useMemo(() => isModified(draft, current), [draft, current]);
  // Modifié autrement que par le choix « Terrain libre » : sinon rien à écrire, la vue Manœuvre s'ouvre directement.
  const dirtyWithoutChoice = useMemo(() => modifiedWithoutChoice(draft, current), [draft, current]);
  // Nouveau dossier : la force et la mission proposées par défaut ne font pas un brouillon ; un nom, un camp ou un
  // terrain libre, si.
  const unsaved = current ? dirty : Boolean(draft.name.trim() || draft.mapSlug || draft.freeTerrain);
  const loadingCurrent = Boolean(exerciseId) && !current;
  const draftKey = draftKeyOf(current?.id);
  useEffect(() => {
    if (loadingCurrent) return; // dossier mémorisé encore en chargement : ne rien écraser
    if (draftOwner !== owner) return; // brouillon d'un autre dossier, remplacé au rendu suivant
    if (unsaved) writeStoredDraft(draftKey, { draft, base, baseVersion, conflicts, rebased });
    else removeStoredDraft(draftKey);
  }, [draft, base, baseVersion, conflicts, rebased, unsaved, draftKey, loadingCurrent, draftOwner, owner]);
  // Déclarée au registre des saisies non enregistrées (audit de persistance M4) : gardée sur ce poste et rétablie
  // d'elle-même, elle ne retient ni la navigation ni la fermeture de l'onglet, mais la libération du poste la cite.
  useUnsavedDraft("mission:draft", unsaved && !loadingCurrent, {
    label: current ? t("Données initiales « {name} »", { name: current.name }) : t("Nouveau dossier"),
    navigation: false,
    unload: false,
  });
  // Plus rien à enregistrer (annulé, ou rejoint par le dossier) : l'avis du report n'a plus lieu d'être.
  useEffect(() => {
    if (!unsaved && rebased) setRebased(false);
  }, [unsaved, rebased]);
  // Champs modifiés des deux côtés, encore ouverts : signalés sous chacun, avec « Reprendre la valeur enregistrée ».
  const conflicted = useMemo(
    () => (current ? openConflicts(conflicts, draft, server) : new Set<FieldKey>()),
    [conflicts, draft, server, current],
  );
  const conflictOn = (...keys: FieldKey[]) => keys.some((key) => conflicted.has(key));
  const takeServer = (key: FieldKey) => setDraft((d) => takeServerFields(d, server, key));

  // Retour de l'écran des ordres de bataille (« Employer dans le dossier ») : le choix entre dans le brouillon,
  // rien n'est enregistré d'office ; « Enregistrer » est l'action attendue.
  useEffect(() => {
    if (!pendingUse || loadingCurrent || draftOwner !== owner) return;
    if ((pendingUse.exerciseId ?? null) !== (current?.id ?? null)) return;
    useOrbatsTarget.getState().consumeUse();
    if (pendingUse.field === "odb") {
      preferredMission.current = draft.parameters.mission;
      setDraft((d) => ({ ...d, parameters: { ...d.parameters, odb: pendingUse.value, mission: null, mode_action: null } }));
      void queryClient.invalidateQueries({ queryKey: ["forces"] });
    } else {
      setDraft((d) => ({
        ...d,
        parameters: {
          ...d.parameters,
          eni_odb: pendingUse.value,
          // Face à l'ennemi générique, l'ennemi reste vide (comme le serveur à l'enregistrement).
          enemy_type: neutral ? null : (pendingUse.enemyType ?? d.parameters.enemy_type),
        },
      }));
      void queryClient.invalidateQueries({ queryKey: ["odbs", "ENI"] });
    }
    notify({ text: t("Ordre de bataille reporté"), detail: t("Enregistrez le dossier pour l'employer."), tone: "info" });
    const timer = window.setTimeout(() => designate(saveButtonRef.current), 450);
    return () => window.clearTimeout(timer);
  }, [pendingUse, loadingCurrent, draftOwner, owner, current?.id]);

  // Retour de la vue « ENI personnalisés » (« Employer dans le dossier ») : l'ENI entre dans le brouillon, rien
  // n'est enregistré d'office ; « Enregistrer » est l'action attendue.
  useEffect(() => {
    if (!pendingCustom || loadingCurrent || draftOwner !== owner) return;
    if ((pendingCustom.exerciseId ?? null) !== (current?.id ?? null)) return;
    useCustomEnemyTarget.getState().consumeUse();
    setDraft((d) => ({
      ...d,
      parameters: {
        ...d.parameters,
        custom_enemy: pendingCustom.enemyId,
        enemy_type: d.parameters.eni_odb ? d.parameters.enemy_type : pendingCustom.baseEnemy,
      },
    }));
    void queryClient.invalidateQueries({ queryKey: ["custom-enemies"] });
    notify({ text: t("ENI personnalisé reporté"), detail: t("Enregistrez le dossier pour l'employer."), tone: "info" });
    const timer = window.setTimeout(() => designate(saveButtonRef.current), 450);
    return () => window.clearTimeout(timer);
  }, [pendingCustom, loadingCurrent, draftOwner, owner, current?.id]);

  // Camp affiché : celui du brouillon, toujours (jamais un camp mémorisé à part, ni après « Annuler les
  // modifications » ou un rechargement).
  const shownMapSlug = useUi((s) => s.mapSlug);
  useEffect(() => {
    if (loadingCurrent || draftOwner !== owner) return;
    if (shownMapSlug !== draft.mapSlug) setMapSlug(draft.mapSlug);
  }, [draft.mapSlug, shownMapSlug, loadingCurrent, draftOwner, owner, setMapSlug]);

  // Force et mission vides (nouveau dossier, autre niveau, autre force) : la première proposée par défaut. Une valeur
  // enregistrée qui n'existe plus (ordre de bataille supprimé, mission renommée) est gardée, montrée « (introuvable) »
  // et son champ désigné : jamais remplacée en silence.
  // Les deux mises à jour se décident aussi sur le brouillon au moment où elles s'appliquent : le brouillon du dossier,
  // adopté dans le même rendu (ouverture, autre dossier), ne reçoit jamais la force par défaut calculée pour le
  // brouillon vide d'avant ni une mission tirée de la liste d'une autre force.
  useEffect(() => {
    const first = forces[0];
    if (!first || draft.parameters.odb) return;
    const listLevel = level;
    setDraft((d) => withDefaultForce(d, first.ref, listLevel));
  }, [forces, draft.parameters.odb]);
  useEffect(() => {
    if (isCentac || missions.length === 0) return;
    const found = missions.find((m) => m.name === draft.parameters.mission);
    if (!found && draft.parameters.mission) return;
    // Compagnie : la liste suit l'ordre alphabétique de la fiche ; la mission par défaut est « S'emparer de ». Posture
    // déjà choisie (autre niveau, autre force) : la mission par défaut de cette posture.
    const posture = draft.parameters.posture as DossierPosture | null | undefined;
    const fallback =
      (posture && defaultMissionFor(missions, posture)) ||
      (level === "COMPAGNIE" && missions.find((m) => m.name === COMPANY_DEFAULT_MISSION)) ||
      missions[0]!;
    const preferred = missions.find((m) => m.name === preferredMission.current);
    const mission = found ?? (preferred && (!posture || preferred.posture === posture) ? preferred : fallback);
    preferredMission.current = null;
    const missionPosture = mission.posture ?? null;
    if (
      mission.name !== draft.parameters.mission ||
      mission.mode !== draft.parameters.mode_action ||
      (missionPosture !== null && missionPosture !== (draft.parameters.posture ?? null))
    ) {
      const seen = { odb: draft.parameters.odb, mission: draft.parameters.mission };
      setDraft((d) => withResolvedMission(d, seen, mission.name, mission.mode, missionPosture ?? undefined));
    }
  }, [missions, isCentac, level, draft.parameters.mission, draft.parameters.mode_action, draft.parameters.posture]);
  // Ennemi du brouillon selon la force choisie (registre B39) : vide face à l'ennemi générique, « Titan » remis au
  // retour vers une force française (`withForceEnemy`, comme le serveur). Changement de dossier : rien n'est déduit
  // de l'état du dossier précédent.
  const seenNeutral = useRef<{ owner: string; neutral: boolean } | null>(null);
  useEffect(() => {
    if (loadingCurrent || draftOwner !== owner) return;
    const was = seenNeutral.current?.owner === owner ? seenNeutral.current.neutral : neutral;
    seenNeutral.current = { owner, neutral };
    setDraft((d) => withForceEnemy(d, neutral, was));
  }, [neutral, loadingCurrent, draftOwner, owner]);
  // Ordre de bataille ennemi d'une faction face à l'ennemi générique : son champ est désigné (action attendue), une
  // fois par dossier et par valeur.
  const designatedLegacy = useRef<string | null>(null);
  useEffect(() => {
    const key = eniLegacy ? `${owner}|${eniOdb}` : null;
    if (!key || designatedLegacy.current === key || welcome) return;
    designatedLegacy.current = key;
    designateSoon(`${attentionSelector("dossier-eni-odb")} [role="combobox"]`, "field", 600);
  }, [eniLegacy, owner, eniOdb, welcome]);
  const unknownForce =
    forcesQuery.isSuccess && forces.length > 0 && Boolean(draft.parameters.odb) && !forces.some((f) => f.ref === draft.parameters.odb);
  const unknownMission =
    !isCentac &&
    missionsQuery.isSuccess &&
    missions.length > 0 &&
    Boolean(draft.parameters.mission) &&
    !missions.some((m) => m.name === draft.parameters.mission);
  const unknownField: "dossier-odb" | "dossier-mission" | null = unknownForce ? "dossier-odb" : unknownMission ? "dossier-mission" : null;
  /** Champ à valeur introuvable : désigné, avec son avis court (à l'ouverture, puis à chaque enregistrement tenté). */
  const designateUnknown = (field: "dossier-odb" | "dossier-mission") => {
    designateSoon(`${attentionSelector(field)} [role="combobox"]`, "field", 600);
    notify({
      text: field === "dossier-odb" ? t("Choisissez l'ordre de bataille de la force.") : t("Choisissez la mission de la force."),
      detail: t("La valeur enregistrée n'existe plus : elle est gardée tant que vous n'en choisissez pas une autre."),
      tone: "info",
    });
  };
  const designatedUnknown = useRef<string | null>(null);
  useEffect(() => {
    const key = unknownField && `${owner}|${unknownField}|${unknownField === "dossier-odb" ? draft.parameters.odb : draft.parameters.mission}`;
    if (!key || designatedUnknown.current === key || welcome) return;
    designatedUnknown.current = key;
    designateUnknown(unknownField!);
  }, [unknownField, owner, draft.parameters.odb, draft.parameters.mission, welcome]);

  // La carte affiche le dispositif du brouillon, dès que force et mission sont résolues
  // (pas de calcul intermédiaire pendant un changement de niveau) ; la mesure et l'analyse du terrain envoient sa
  // météo (« auto » en CENTAC), toujours celle du brouillon affiché.
  useEffect(() => {
    if (!draftReady(draft)) return;
    setDeploymentParams(deploymentParamsOf(draft, current));
  }, [draft, current, setDeploymentParams]);
  useEffect(() => {
    if (loadingCurrent || draftOwner !== owner) return;
    setWeatherKey(weatherKey(draft.parameters));
  }, [draft.parameters, loadingCurrent, draftOwner, owner, setWeatherKey]);

  // Ce que l'enregistrement du brouillon effacerait (registre K20), d'après l'aperçu calculé sur le dossier.
  const preview = useDeployment();
  const previewNotices = current && dirtyWithoutChoice && !preview.isPlaceholderData ? (preview.data?.notices ?? []) : [];
  const losses = [
    previewNotices.includes(CORRIDOR_CLEARED) && t("Le fuseau personnalisé sera remplacé par celui de la nouvelle mission."),
    previewNotices.includes(CORRIDOR_CLEARED_CAMP) && t("Le fuseau personnalisé sera effacé : autre camp."),
    previewNotices.includes(MISSIONS_CLEARED) && t("Les missions choisies seront effacées."),
    previewNotices.includes(ORIENTATION_CLEARED) && t("Le sens de la manœuvre sera effacé : autre terrain ou rotation CENTAC."),
    // Sens gardé mais qui ne tient plus dans le camp pour la nouvelle mission (registre K20) : annoncé avant
    // l'enregistrement.
    current && dirtyWithoutChoice && !preview.isPlaceholderData && orientationLost(preview.data?.corridor?.orientation),
  ].filter((text): text is string => Boolean(text));

  const { data: mapDetail } = useQuery({
    queryKey: ["map", draft.mapSlug],
    queryFn: () => api.map(draft.mapSlug!),
    enabled: Boolean(draft.mapSlug),
  });

  // Camps partagés et terrains libres de la licence (manœuvre libre, étape 2) ; aucun camp au niveau corps d'armée.
  const camps = level === "CORPS" ? [] : maps.filter((m) => m.kind !== "user");
  // Zones de la licence avec l'état de leur terrain (liste des zones, relue toutes les 5 s tant que l'une se prépare).
  const { data: zoneList } = useZoneList(true, true);
  const corpsLevel = level === "CORPS";
  // Corps d'armée : seuls les terrains préparés à son échelle (300 × 600 km) le portent.
  const zones = zoneChoices(maps, zoneList).filter((z) => !corpsLevel || z.zone?.level === "CORPS");
  const listsLoaded = mapsLoaded && zoneList !== undefined;
  // Règle d'offre unique (Données initiales et vue Manœuvre) : serveur ouvert, carte mondiale, hors CENTAC.
  const offer = useFreeTerrainOffer(isCentac);
  const layout = layoutOf({ centac: isCentac, offered: offer.offered });
  /** Nature d'une carte : camp, terrain de la licence (liste des cartes, puis liste des zones), ou inconnue. */
  const kindOf = (slug: string | null | undefined): MapKind => {
    if (!slug) return null;
    const listed = maps.find((m) => m.slug === slug);
    if (listed) return listed.kind === "user" ? "user" : "camp";
    return zones.some((z) => z.slug === slug) ? "user" : null;
  };
  const mapKind = kindOf(draft.mapSlug);
  const savedNoMap = Boolean(current && !current.map_slug);
  // « Terrain libre » enregistré sur un dossier qui garde sa carte jusqu'au nouveau terrain : toujours affiché.
  const pendingFree =
    offer.offered && !draft.freeTerrain && pendingTerrain(terrainFor, current?.id ?? null, current?.map_slug ?? null, draft.mapSlug);
  const site: Site = corpsLevel ? "free" : (siteChoice ?? derivedSite({ freeTerrain: draft.freeTerrain, pendingFree, savedNoMap, mapKind }));
  const sites: readonly Site[] = corpsLevel ? ["free"] : SITES;
  // « Nouveau terrain » : à placer dans la vue Manœuvre (choisi, en attente, ou dossier enregistré sans carte).
  const newTerrain = layout === "cards" && site === "free" && (draft.freeTerrain || pendingFree || (savedNoMap && !draft.mapSlug));
  const kind = saveKind({ saved: Boolean(current), newTerrain, dirtyWithoutChoice });
  // Dossier en lecture seule pour ce compte (comptes nominatifs, docs/17 §3.2) : « Enregistrer » désigne le bandeau.
  const lockedDossier = readOnly(current);
  // Dernier camp, dernier terrain et dernier lieu nommé choisis : chaque carte de choix les retrouve pendant la saisie.
  const lastCampRef = useRef<string | null>(null);
  const lastTerrainRef = useRef<string | null>(null);
  const lastPlaceRef = useRef<NamedPlace | null>(null);
  useEffect(() => {
    lastCampRef.current = null;
    lastTerrainRef.current = null;
    lastPlaceRef.current = null;
  }, [current?.id]);
  // Brouillon restauré avec « Nouveau terrain » alors que le terrain libre n'est plus proposé : jamais un choix
  // impossible à valider.
  useEffect(() => {
    if (offer.known && !offer.offered && draft.freeTerrain) {
      setDraft((d) => ({ ...d, freeTerrain: false, mapSlug: current?.map_slug ?? d.mapSlug, parameters: withPlace(d.parameters, null) }));
    }
  }, [offer.known, offer.offered, draft.freeTerrain, current?.map_slug]);
  // Fuseau horaire proposé par la zone libre du dossier (heure légale au GDH de début, K16) : posé d'office tant que
  // le fuseau n'est pas choisi (`unchosenTimeZone`), sinon proposé par « Appliquer », jamais imposé.
  // Lieu nommé d'un nouveau terrain : l'heure légale de la localité choisie (retenue le temps de la saisie).
  const [placeZones, setPlaceZones] = useState<(PlaceZones & { ref: string }) | null>(null);
  const draftPlace = newTerrain ? (draft.parameters.place ?? null) : null;
  const placeLetters = draftPlace && placeZones && placeZones.ref === draftPlace.ref && isZoneLetter(placeZones.winter) ? placeZones : null;
  const zoneLetter = proposedTimeZone(
    placeLetters
      ? { time_zone: placeLetters.winter as ZoneLetter, time_zone_summer: isZoneLetter(placeLetters.summer) ? placeLetters.summer : null }
      : zoneList?.find((z) => z.slug === draft.mapSlug),
    draft.parameters.start_dtg,
  );
  // Sans fuseau, l'heure est Z partout (OPO, DIREX, exports) : le sélecteur le montre.
  const offerZoneLetter = zoneLetter !== null && zoneLetter !== (draft.parameters.time_zone ?? "Z");

  const chooseMap = (slug: string | null) => {
    // Un autre choix remplace le « Terrain libre » en attente ; le même le reprend (le bouton rouvre l'étape Terrain).
    setTerrainFor(null);
    if (slug === FREE_TERRAIN) {
      // « Nouveau terrain » : un choix du brouillon. La carte du dossier enregistré (ou aucune) reste affichée
      // jusqu'à la préparation du nouveau terrain ; le lieu nommé quitté plus tôt revient. La météo est gardée : elle
      // se ramène au climat du lieu dès qu'il est nommé (réajustement « à cet endroit », avec sa cause).
      const kept = current?.map_slug ?? null;
      setDraft((d) => ({
        ...d,
        freeTerrain: true,
        mapSlug: kept,
        parameters: withPlace(d.parameters, d.parameters.place ?? lastPlaceRef.current),
      }));
      setMapSlug(kept);
      return;
    }
    // Un camp ou un terrain de la licence : le lieu nommé du nouveau terrain est retenu (retour à « Nouveau terrain »),
    // jamais enregistré avec une autre carte.
    if (draft.parameters.place) lastPlaceRef.current = draft.parameters.place;
    // Zone libre choisie et fuseau horaire pas encore choisi : l'heure légale de la zone au GDH de début (K16),
    // comme à la création de la zone d'un dossier (serveur) ; un choix délibéré (Z…) est gardé.
    const letter = proposedTimeZone(
      zoneList?.find((z) => z.slug === slug),
      draft.parameters.start_dtg,
    );
    setDraft((d) => {
      const parameters = withPlace(d.parameters, null);
      return {
        ...d,
        mapSlug: slug,
        freeTerrain: false,
        parameters: letter && unchosenTimeZone(d.parameters.time_zone) ? { ...parameters, time_zone: letter } : parameters,
      };
    });
    setMapSlug(slug);
  };

  /** Carte de choix du lieu (clic, toucher, flèches, Début, Fin) : chacune retrouve le camp ou le terrain choisi
   *  auparavant ; « Camp de manœuvre » sans camp mémorisé ne touche pas à la carte affichée (la liste reste vide, le
   *  bouton la désignera). Aucune liste ne s'ouvre d'elle-même. */
  const pick = (next: Site) => {
    if (next === site) return;
    setSiteChoice(next);
    const savedCamp = kindOf(current?.map_slug) === "camp" ? (current?.map_slug ?? null) : null;
    const { memory, target } = pickSite(
      next,
      { freeTerrain: draft.freeTerrain, mapKind, mapSlug: draft.mapSlug, savedCamp },
      { camp: lastCampRef.current, terrain: lastTerrainRef.current },
    );
    lastCampRef.current = memory.camp;
    lastTerrainRef.current = memory.terrain;
    if (target === "new") chooseMap(FREE_TERRAIN);
    else if (target) chooseMap(target);
    else {
      setTerrainFor(null);
      if (draft.parameters.place) lastPlaceRef.current = draft.parameters.place;
      setDraft((d) => ({ ...d, freeTerrain: false, parameters: withPlace(d.parameters, null) }));
    }
  };
  const chooseCamp = (slug: string) => {
    if (slug) lastCampRef.current = slug;
    chooseMap(slug || null);
  };
  const chooseTerrain = (slug: string) => {
    lastTerrainRef.current = slug === FREE_TERRAIN ? null : slug || null;
    chooseMap(slug || null);
  };

  /** Lieu du nouveau terrain choisi dans le champ « Lieu du nouveau terrain » : rangé dans le brouillon ; l'heure
   *  légale d'une localité est posée d'office tant que le fuseau n'est pas choisi (sinon « Appliquer » la propose). */
  const choosePlace = (place: NamedPlace | null, zones: PlaceZones | null) => {
    setPlaceZones(place?.ref && zones ? { ...zones, ref: place.ref } : null);
    const letter = place && zones ? proposedTimeZone({ time_zone: isZoneLetter(zones.winter) ? zones.winter : null, time_zone_summer: isZoneLetter(zones.summer) ? zones.summer : null }, draft.parameters.start_dtg) : null;
    setDraft((d) => {
      const parameters = withPlace(d.parameters, place);
      return { ...d, parameters: letter && unchosenTimeZone(d.parameters.time_zone) ? { ...parameters, time_zone: letter } : parameters };
    });
  };

  // Valeurs et textes du champ de suite.
  const campValue = mapKind === "camp" ? (draft.mapSlug ?? "") : "";
  const chosenZone = newTerrain ? undefined : zones.find((m) => m.slug === draft.mapSlug);
  const missingTerrain = draft.mapSlug && !newTerrain && listsLoaded && mapKind === null ? draft.mapSlug : null;
  const newlyChosen = draft.mapSlug !== (current?.map_slug ?? null);
  const savedMapName = current?.map_slug ? (maps.find((m) => m.slug === current.map_slug)?.name ?? null) : null;
  // Nouveau terrain sur un dossier posé : ce que le terrain effacera, dit sous le choix (le champ du lieu dit le reste).
  const newTerrainHint = savedMapName
    ? t("{carte} reste la carte du dossier jusqu'à la préparation du nouveau terrain ; déplacements, ponts détruits et graphiques tactiques seront alors effacés.", {
        carte: savedMapName,
      })
    : undefined;
  const zoneStateHint = !chosenZone
    ? undefined
    : chosenZone.status === "ready"
      ? undefined
      : chosenZone.status === "failed" || chosenZone.status === "cancelled"
        ? offer.reason === "closed"
          ? t("Préparation interrompue : la relance attend la réouverture du terrain libre sur ce serveur.")
          : newlyChosen
            ? t("Préparation interrompue : à l'enregistrement, la vue Manœuvre s'ouvre sur « Réessayer ».")
            : t("Préparation interrompue : relancez-la dans la vue Manœuvre.")
        : newlyChosen
          ? t("En préparation : à l'enregistrement, la vue Manœuvre montre son avancement ; unités et missions arriveront quand il sera prêt.")
          : t("En préparation : unités et missions arriveront quand il sera prêt.");
  const terrainHint = newTerrain ? newTerrainHint : zoneStateHint;
  // Lien vers la vue Manœuvre : une carte réelle choisie pour le site affiché, pas pour un nouveau terrain (le bouton
  // principal fait ce travail), jamais en CENTAC.
  const showManoeuvreLink =
    layout !== "centac" && !newTerrain && (layout === "list" ? Boolean(draft.mapSlug) && mapKind !== null : site === "camp" ? Boolean(campValue) : mapKind === "user");
  // Profil du bas : celui du lieu choisi pour la carte de choix affichée — ni la carte qu'un nouveau terrain remplacera,
  // ni un terrain resté affiché sous « Camp de manœuvre » (liste des camps vide), ni un camp sous « Terrain libre ».
  const showMapProfile = mapProfileShown({ newTerrain, layout, site, mapKind });
  // Météo « Choisie » (chantier « Données initiales balisées + lieu nommé ») : offerte dès que le lieu est connu — un
  // camp, un terrain de la licence (climat estimé tant qu'il se prépare), ou le lieu nommé d'un nouveau terrain (climat
  // estimé d'après ses coordonnées, confirmé quand le terrain sera prêt). Sans lieu, la liste du lieu ou le champ du
  // lieu est désigné : tout se règle ici, sans quitter les Données initiales.
  const weatherWait = weatherWaits({ centac: isCentac, newTerrain, mapSlug: draft.mapSlug, placeNamed: Boolean(draftPlace) });
  const weatherPending: WeatherPending | null =
    weatherWait === "name"
      ? {
          hint: t("Nommez d'abord le lieu du nouveau terrain, plus haut : la météo proposée dépend de son climat."),
          onBlocked: () => designatePlace(),
        }
      : weatherWait === "choose"
        ? {
            hint: t("Choisissez d'abord le lieu de l'exercice, plus haut : la météo proposée dépend de son climat et de la date."),
            onBlocked: () => designateChoice(t("Choisissez d'abord le lieu de l'exercice.")),
          }
        : null;
  // Écart de la météo du brouillon sur le climat du lieu (verdict du réglage Météo) : l'étape « Date et météo » est à
  // revoir.
  const [weatherGap, setWeatherGap] = useState(false);

  // Étapes balisées (`missionSteps`) : état de chaque rubrique, étape attendue, préalables du bouton principal.
  const campChosen = layout === "cards" ? (site === "camp" ? mapKind === "camp" : Boolean(draft.mapSlug)) : Boolean(draft.mapSlug);
  const lieu: StepsPlace =
    layout === "centac"
      ? { kind: "centac" }
      : newTerrain
        ? { kind: "new", named: Boolean(draftPlace) }
        : !campChosen
          ? { kind: "camp", chosen: false }
          : missingTerrain
            ? { kind: "terrain", status: "missing" }
            : mapKind === "user"
              ? {
                  kind: "terrain",
                  status:
                    !chosenZone || chosenZone.status === "ready"
                      ? "ready"
                      : chosenZone.status === "failed" || chosenZone.status === "cancelled"
                        ? "interrupted"
                        : "preparing",
                }
              : { kind: "camp", chosen: true };
  const stepped = missionSteps({
    level,
    saved: Boolean(current),
    readOnly: lockedDossier,
    writes: kind !== "place",
    named: Boolean(draft.name.trim()),
    rotation: draft.parameters.centac_rotation,
    mission: draft.parameters.mission,
    unknownForce,
    unknownMission,
    lieu,
    weatherGap,
    eniLegacy,
    customEnemyMissing: Boolean(chosenCustom) && !chosenCustomSummary && customListing.isSuccess,
    loading: {
      forces: forcesQuery.isPending || (missionsQuery.isFetching && !missionsQuery.data),
      lieu: !listsLoaded || !offer.known,
      enemies: !isCentac && (!eniListing || customListing.isPending),
    },
  });
  const { steps, next: nextStep, missing } = stepped;
  // Champs à renseigner en surbrillance jusqu'à leur saisie (préalables du bouton, le champ attendu plus marqué).
  const awaited = awaitedFields(stepped);
  const awaitingOf = (field: StepField) => awaitingFor(awaited, field);
  const stepOf = (key: Step["key"]) => steps.find((step) => step.key === key)!;

  // Parcours guidé (refonte du 02/11/2026) : un seul volet ouvert à la fois — une étape, ou les options ; les étapes
  // d'un nouveau dossier se valident une à une (« Continuer »). Dossier enregistré : tout est validé, replié en
  // résumés, sauf l'étape d'un préalable manquant, ouverte d'office.
  const [open, setOpen] = useState<Fold | null>(() => (current ? null : "dossier"));
  const [confirmed, setConfirmed] = useState<ReadonlySet<StepKey>>(() => new Set());
  const openRef = useRef(open);
  openRef.current = open;
  const reset = useRef<string | null>(null);
  const settled = useRef<string | null>(null);
  useEffect(() => {
    if (loadingCurrent || draftOwner !== owner) return;
    if (reset.current !== owner) {
      reset.current = owner;
      setConfirmed(new Set());
      setOpen(current ? null : "dossier");
    }
    // Étape ouverte d'office une fois les listes reçues (un préalable n'est connu qu'avec elles).
    if (settled.current === owner || steps.some((step) => step.state === "pending")) return;
    settled.current = owner;
    if (current) setOpen(firstOpenStep(stepped, true));
  }, [loadingCurrent, draftOwner, owner, current, steps, stepped]);
  const saved = Boolean(current);
  const reached = (key: StepKey) => saved || confirmed.has(key) || open === key;
  // Dossier enregistré sans carte alors que le terrain libre n'est pas proposé : la cause est dite (E18).
  const unavailableNote =
    layout === "list" && offer.known && savedNoMap && !draft.mapSlug
      ? offer.reason === "closed"
        ? t("Le terrain libre n'est plus ouvert sur ce serveur : choisissez un lieu dans la liste.")
        : offer.reason === "no-world-map"
          ? t("Le terrain libre demande la carte mondiale, absente de ce serveur : choisissez un lieu dans la liste.")
          : null
      : null;

  const chooseLevel = (next: Level) => {
    if (next === level) return;
    // CENTAC : aucun réglage météo (la météo de saison du camp se retouche dans l'ordre de la rotation) ; une météo
    // choisie est retirée, jamais appliquée en cachette, et l'avis le dit (le même que celui du serveur).
    const weatherCleared = next === "CENTAC" && weatherState(draft.parameters) === "custom" ? tServer(WEATHER_CENTAC) : null;
    // Un ordre de bataille ennemi fait face à un seul niveau : un autre niveau le retire (ennemi de référence).
    if (draft.parameters.eni_odb) {
      notify({
        text: tServer(ENI_ODB_CLEARED),
        // Face à l'ennemi générique, aucun « ennemi de référence » n'est évoqué (registre B39).
        detail: [neutral ? t("L'ennemi par défaut du nouveau niveau le remplace.") : t("L'ennemi de référence du nouveau niveau le remplace."), weatherCleared]
          .filter(Boolean)
          .join(" "),
        tone: "info",
      });
    } else if (weatherCleared) {
      notify({ text: weatherCleared, tone: "info" });
    }
    // Comme sur l'iPad : CENTAC impose l'ennemi Titan et le camp de Mailly.
    const reset = { level: next, odb: null, mission: null, mode_action: null, eni_odb: null };
    preferredMission.current = null;
    if (next === "CENTAC") {
      // Le dossier prend le nom de la rotation (registre A122) : un nom déjà saisi qui la cite en donne le numéro.
      const kept = draft.parameters.centac_rotation;
      const code = isRotationCode(kept) ? kept : (deducedRotation(draft.name) ?? kept);
      setDraft((d) => ({
        ...d,
        name: centacName(code),
        mapSlug: CENTAC_MAP,
        freeTerrain: false,
        // Rotation CENTAC figée : pas de posture ; météo de saison (« auto »), comme le serveur l'enregistre ; ni
        // lieu nommé (camp imposé).
        parameters: {
          ...withPlace(withSeasonalWeather(d).draft.parameters, null),
          ...reset,
          enemy_type: "Titan",
          posture: null,
          enemy_profile: null,
          centac_rotation: code,
        },
      }));
      setMapSlug(CENTAC_MAP);
      // Le champ du numéro remplace celui du nom : désigné pour la saisie (nouveau dossier sans numéro).
      if (!current && !isRotationCode(code)) designateSoon(attentionSelector(CENTAC_ROTATION_FIELD), "field", 350);
    } else if (next === "CORPS") {
      // Corps d'armée : terrain libre seulement ; un terrain déjà préparé à son échelle est gardé, sinon un nouveau.
      const keep = mapKind === "user" && zoneList?.find((z) => z.slug === draft.mapSlug)?.level === "CORPS";
      setDraft((d) => ({
        ...d,
        ...(keep ? {} : { freeTerrain: true, mapSlug: null }),
        parameters: { ...d.parameters, ...reset },
      }));
      if (!keep) setMapSlug(null);
    } else {
      setDraft((d) => ({ ...d, parameters: { ...d.parameters, ...reset } }));
    }
    // Hors CENTAC, le nom redevient libre : le nom provisoire d'un nouveau dossier ne lui reste pas.
    if (level === "CENTAC" && next !== "CENTAC") setDraft((d) => ({ ...d, name: nameLeavingCentac(d.name, Boolean(current)) }));
    setSiteChoice(null);
  };

  const awaitingRef = useRef(false);
  // Vue Manœuvre ouverte à l'étape Terrain, recherche de lieu prête et « Tracer l'axe » désigné (parcours guidé).
  // Dossier qui garde sa carte (un camp, ou le terrain libre que le nouveau remplacera) : choix mémorisé jusqu'au
  // nouveau terrain (`terrainFor`), pour la liste et pour la vue Manœuvre.
  const openTerrainStep = (keepsMap = Boolean(current?.map_slug)) => {
    if (keepsMap && current) setTerrainFor(current.id);
    setView("manoeuvre");
    requestZoneStart(true);
  };
  const saveIntent = (): SaveIntent => ({
    freeTerrain: newTerrain,
    previousMap: current?.map_slug ?? null,
    created: !current,
    mapReady: !chosenZone || chosenZone.status === "ready",
  });
  const save = useMutation({
    mutationFn: async (_intent: SaveIntent) => {
      if (current) {
        // Langue : jamais écrite ici, les documents suivent l'interface du poste qui les lit (docs/13).
        return api.updateExercise(current, {
          name: draft.name,
          // Corps d'armée sur un camp enregistré : le dossier quitte le camp jusqu'à son nouveau terrain.
          map_slug: draft.parameters.level === "CORPS" && kindOf(draft.mapSlug) === "camp" ? null : draft.mapSlug,
          parameters: withPlace(draft.parameters, newTerrain ? draft.parameters.place : null),
        });
      }
      return api.createExercise({
        name: draft.name,
        // Langue de rédaction du nouveau dossier : celle de l'interface du poste qui le crée (docs/13).
        language: lang(),
        // « Nouveau terrain » : dossier créé sans carte, le terrain se place ensuite dans la vue Manœuvre.
        map_slug: newTerrain ? null : draft.mapSlug,
        parameters: withPlace(draft.parameters, newTerrain ? draft.parameters.place : null),
      });
    },
    onSuccess: (ex, intent) => {
      setError(null);
      const next = afterSave(intent, ex.map_slug);
      // Niveau changé : un OPO finalisé garde l'ancien (annexes de la division comprises) jusqu'à sa réouverture.
      const levelChanged = Boolean(current) && current?.parameters.level !== ex.parameters.level;
      // OPO recomposés : « Voir les changements », sinon « Voir l'ordre » (paragraphes à revoir) ou « Ouvrir l'OPO ».
      // CENTAC : un OPO validé est déjà joué, jamais « à rouvrir » (le serveur le dit aussi, `orders.centac`).
      const centac = ex.parameters.level === "CENTAC";
      const refreshed = ex.refreshed ? { ...ex.refreshed, centac: centac || undefined } : null;
      const ordersActions = refreshed ? orderActions(undefined, refreshed) : {};
      const ordersText = refreshed ? ordersNote(refreshed, { levelChanged, source: "dossier" }) : "";
      if (next === "terrain") {
        notify({
          text: current ? t("Dossier enregistré") : t("Dossier créé"),
          detail: t("Cherchez un lieu, puis tracez l'axe du fuseau sur la carte."),
          tone: "ok",
        });
      } else if (next === "zone-wait") {
        notify({ text: current ? t("Dossier enregistré") : t("Dossier créé"), tone: "ok" });
      } else if (awaitingRef.current) {
        notify({
          text: current ? t("Dossier enregistré") : t("Dossier créé"),
          detail: [
            t("Faites glisser une unité ou une ligne sur la carte pour la déplacer."),
            ordersText,
          ]
            .filter(Boolean)
            .join(" "),
          tone: "ok",
          ...ordersActions,
        });
      } else if (ordersText) {
        // Données initiales changées : les OPO ont été recomposés (docs/18 §5.3), un OPO validé dont le texte change
        // remis en rédaction ; l'avis nomme les sections mises à jour et mène aux changements surlignés.
        notify({
          text: t("Dossier enregistré"),
          detail: ordersText,
          tone: "ok",
          ...ordersActions,
        });
      }
      if (ex.refreshed) refreshOrders(queryClient, ex.id);
      // Météo, date ou fuseau changés (choix ramené au terrain par le serveur compris) : les vues qui lisent la météo
      // appliquée la relisent.
      const before = current?.parameters;
      if (
        before &&
        !sameValue(
          [before.weather_scenario, before.weather_custom ?? null, before.start_dtg, before.time_zone],
          [ex.parameters.weather_scenario, ex.parameters.weather_custom ?? null, ex.parameters.start_dtg, ex.parameters.time_zone],
        )
      )
        refreshWeatherViews(queryClient, ex.id);
      // Rotation CENTAC renommée (registre A119) : en-têtes des OPO, WINGO et FRAGO et guide de la rotation relus.
      if (centac && current?.parameters.centac_rotation !== ex.parameters.centac_rotation) {
        refreshOrders(queryClient, ex.id);
        void queryClient.invalidateQueries({ queryKey: ["frago-layout", ex.id] });
      }
      removeStoredDraft(draftKey);
      removeStoredDraft(draftKeyOf(ex.id));
      // Saisie enregistrée : le formulaire repart du dossier tel que le serveur l'a écrit (rien à reporter).
      adopt({ draft: fromExercise(ex), base: fromExercise(ex), baseVersion: ex.version, conflicts: {}, rebased: false });
      setDraftOwner(ex.id);
      // Le dossier entre tout de suite dans la liste en cache : il est ouvert sans attendre le rechargement.
      queryClient.setQueryData<Exercise[]>(["exercises"], (list = []) =>
        list.some((e) => e.id === ex.id) ? list.map((e) => (e.id === ex.id ? ex : e)) : [ex, ...list],
      );
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      setExercise(ex.id, ex.map_slug);
      setSiteChoice(null);
      // Camp ou terrain prêt : cadrage sur le dispositif, annotations et unités affichées ; « Nouveau terrain » : vue
      // Manœuvre à l'étape Terrain ; terrain de la licence pas prêt : vue Manœuvre sur son avancement ou « Réessayer ».
      if (next === "terrain") openTerrainStep(Boolean(ex.map_slug));
      else if (next === "zone-wait") showZoneWait(zoneList?.find((z) => z.slug === ex.map_slug) ?? null);
      else if (next === "frame") frameDossier();
    },
    onError: (e) => {
      // Dossier modifié entre-temps (412, ou conflit au moment de l'écriture) : il est relu et la saisie y est
      // reportée champ par champ (effet de version, avis « Le dossier a changé… ») ; jamais « rechargez-le », qui
      // la perdrait.
      if (current && e instanceof ApiError && (e.status === 412 || e.status === 409)) {
        const stale = current.version;
        setError(null);
        void queryClient.refetchQueries({ queryKey: ["exercises"], exact: true }).then(() => {
          const fresh = queryClient.getQueryData<Exercise[]>(["exercises"])?.find((x) => x.id === current.id);
          if (!fresh || fresh.version === stale)
            setError(t("Le dossier a changé depuis votre saisie, mais sa nouvelle version n'a pas pu être lue : réessayez d'enregistrer dans un instant."));
        });
        return;
      }
      setInputError(e instanceof ApiError && (e.status === 409 || e.status === 422));
      setError(e instanceof ApiError ? e.message : t("Enregistrement impossible"));
    },
  });

  // Demande d'enregistrement (déplacement refusé sur la carte, lien d'une fiche) : enregistrer directement
  // si elle le demande et que le formulaire le permet ; sinon désigner l'action attendue. Le bouton clignote,
  // puis pulse doucement jusqu'à l'enregistrement ; sans nom, c'est le champ du nom qui est désigné.
  const saveBarRef = useRef<HTMLDivElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const handledRequest = useRef(0);
  const [awaitingSave, setAwaitingSave] = useState(false);
  useEffect(() => {
    if (!saveRequest || loadingCurrent || handledRequest.current === saveRequest.at) return;
    if (Date.now() - saveRequest.at > SAVE_REQUEST_TTL_MS) return;
    handledRequest.current = saveRequest.at;
    // Déjà enregistré (seul le choix « Terrain libre » diffère, au plus) : les déplacements se débloquent d'eux-mêmes.
    if (current && !dirtyWithoutChoice) return;
    setAwaitingSave(true);
    if (saveRequest.submit && missing.length === 0 && !save.isPending) {
      // Demande venue de la carte : enregistrer, sans ouvrir la vue Manœuvre pour un dossier enregistré (le choix
      // « Terrain libre » attend son propre bouton).
      save.mutate(current ? { ...saveIntent(), freeTerrain: false } : saveIntent());
      return;
    }
    // Après le rendu de la vue (ouverte par la demande), pour que défilement et animation portent.
    // Préalable manquant : le premier, dans l'ordre des étapes ; sinon le bouton.
    window.requestAnimationFrame(() => {
      if (missing[0]) {
        designateStep(missing[0]);
      } else {
        saveBarRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        flashAttention(saveButtonRef.current, "button");
      }
    });
  }, [saveRequest, loadingCurrent, current, dirtyWithoutChoice, missing, save]);
  useEffect(() => {
    if (!unsaved) setAwaitingSave(false);
  }, [unsaved]);
  awaitingRef.current = awaitingSave;

  // « Nouveau dossier » depuis l'accueil : le curseur attend le nom, une fois l'accueil refermé.
  const handledFocus = useRef<number | null>(null);
  useEffect(() => {
    if (!nameFocus || welcome || current || handledFocus.current === nameFocus) return;
    handledFocus.current = nameFocus;
    nameRef.current?.focus({ preventScroll: true });
    flashAttention(nameRef.current, "field");
  }, [nameFocus, welcome, current]);

  // Suppression sans retour : confirmée comme sur l'iPad (« Toutes les données relatives au dossier d'exercice
  // seront supprimées »), avec le téléchargement proposé avant.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.deleteExercise(current!),
    // Dossier modifié entre-temps sur un autre poste (412) : relu, la confirmation suivante porte sur la version relue.
    onError: (error) => {
      if (error instanceof ApiError && error.status === 412) void queryClient.invalidateQueries({ queryKey: ["exercises"] });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      refreshAfterDossiersRemoved(queryClient);
      setConfirmDelete(false);
      setExercise(null, null);
    },
  });

  // Ordres de bataille construits par l'utilisateur d'abord, sous leur propre groupe (registre B36).
  const formId = useId();
  // Nom manquant : « Créer le dossier » reste cliquable et désigne le champ du nom (R12). `quiet` : désignation
  // demandée par la phrase « Ensuite », qui dit déjà quoi faire (pas d'avis en plus).
  const designateName = (quiet = false) => {
    nameRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }); // intitulé compris
    designate(nameRef.current, "field");
    if (!quiet) notify({ text: t("Nommez le dossier pour l'enregistrer."), tone: "info" });
  };
  // Lieu de l'exercice à choisir : la liste attendue (camp ou terrain) est désignée, à défaut le groupe de cartes (R12).
  const choiceRef = useRef<HTMLDivElement>(null);
  const designateChoice = (text: string | null) => {
    const field = choiceRef.current?.querySelector<HTMLElement>('[data-designate] [role="combobox"]') ?? null;
    const target = field ?? choiceRef.current?.querySelector<HTMLElement>('[role="radiogroup"]') ?? choiceRef.current;
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
    designate(target, field ? "field" : "ring");
    if (text) notify({ text, tone: "info" });
  };
  // Lieu du nouveau terrain à nommer : son champ prend le curseur (R12).
  // Le cadre du champ (celui de sa surbrillance) clignote, pas le texte qu'il contient ; le curseur va au texte.
  const designatePlace = (quiet = false) => {
    const input = document.querySelector<HTMLElement>(`${attentionSelector(PLACE_FIELD)} input`);
    input?.scrollIntoView({ block: "center", behavior: "smooth" });
    designate(input?.closest("label") ?? input ?? attentionSelector(PLACE_FIELD), "field");
    input?.focus({ preventScroll: true });
    if (!quiet) notify({ text: t("Nommez le lieu du nouveau terrain : ville, coordonnées ou MGRS."), tone: "info" });
  };
  // Numéro de rotation CENTAC mal formé (registre A119) : le champ est désigné, son erreur affichée.
  const [rotationTouched, setRotationTouched] = useState(false);
  const designateRotation = () => {
    setRotationTouched(true);
    designate(document.querySelector<HTMLElement>(attentionSelector(CENTAC_ROTATION_FIELD)), "field");
    const missing = !current && !draft.parameters.centac_rotation;
    notify({
      text: t(rotationError(draft.parameters.centac_rotation) ?? (missing ? "Saisissez le numéro de la rotation pour créer le dossier : il lui donne son nom." : "Saisissez la rotation sous la forme NN/AA, par exemple 08/26.")),
      tone: "info",
    });
  };
  /** Étape attendue (préalable du bouton, phrase « Ensuite ») : le champ, le bouton, l'avancement du terrain ou la
   *  rubrique qu'elle désigne, avec son avis court — sauf depuis la phrase « Ensuite » (`quiet`), qui le dit déjà. */
  const designateStep = (step: Step, quiet = false) => {
    const target = step.target;
    // Étape repliée : elle s'ouvre d'abord (les autres se replient), la désignation suit son ouverture.
    if (target.kind === "save" || target.kind === "zone" || openRef.current === step.key) return designateStepNow(step, quiet);
    setOpen(step.key);
    window.setTimeout(() => designateStepNow(step, quiet), REVEAL_MS);
  };
  const designateStepNow = (step: Step, quiet = false) => {
    const say = (text: string) => {
      if (!quiet) notify({ text, tone: "info" });
    };
    const combobox = (field: string, text: string) => {
      designate(document.querySelector<HTMLElement>(`${attentionSelector(field)} [role="combobox"]`), "field");
      say(text);
    };
    const target = step.target;
    if (target.kind === "save") {
      saveBarRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      designate(saveButtonRef.current, "button");
      return;
    }
    if (target.kind === "zone") {
      // Terrain de la licence en préparation ou interrompu : son avancement, ou « Réessayer », dans la vue Manœuvre.
      showZoneWait(chosenZone?.zone ?? null);
      return;
    }
    if (target.kind === "section") {
      const section = document.querySelector<HTMLElement>(`[data-fold="${step.key}"]`);
      scrollFoldIntoView(section);
      designate(section, "ring");
      return;
    }
    switch (target.field) {
      case "dossier-name":
        return designateName(quiet);
      case "dossier-centac-rotation":
        return designateRotation();
      case "dossier-camp":
        return designateChoice(
          quiet
            ? null
            : step.state === "todo" && lieu.kind === "terrain"
              ? t("Choisissez un terrain.")
              : layout === "list" && zones.length > 0
                ? t("Choisissez un camp ou un terrain.")
                : t("Choisissez un camp."),
        );
      case "dossier-place":
        return designatePlace(quiet);
      case "dossier-odb":
      case "dossier-mission":
        if (step.state === "review" && !quiet) return designateUnknown(target.field);
        return combobox(target.field, target.field === "dossier-odb" ? t("Choisissez l'ordre de bataille de la force.") : t("Choisissez la mission de la force."));
      case "dossier-weather":
        designate(attentionSelector(WEATHER_FIELD), "ring");
        return say(t("Réglez la météo de l'exercice."));
      case "dossier-enemy":
        return combobox("dossier-enemy", t("Choisissez l'ennemi."));
      case "dossier-eni-odb":
        return combobox("dossier-eni-odb", t("Choisissez l'ordre de bataille ennemi."));
      case "dossier-drones":
        return designateDrones();
    }
  };

  // Accordéon : ouvrir un volet (les autres se replient) et l'amener sous le rail une fois déplié.
  const formRef = useRef<HTMLFormElement>(null);
  const openFold = (fold: Fold | null) => {
    setOpen(fold);
    if (!fold) return;
    // Après le rendu qui ouvre le volet (minuterie plutôt qu'image : une page en arrière-plan n'en dessine pas) : à
    // la fin de son dépliage, puis une dernière fois, le volet replié en même temps ayant fini de rendre sa hauteur.
    window.setTimeout(() => {
      const section = formRef.current?.querySelector<HTMLElement>(`[data-fold="${fold}"]`);
      const body = section?.querySelector<HTMLElement>(".gx-collapse");
      const onEnd = (event: TransitionEvent) => {
        if (event.target !== body || event.propertyName !== "grid-template-rows") return;
        body?.removeEventListener("transitionend", onEnd);
        scrollFoldIntoView(section);
      };
      body?.addEventListener("transitionend", onEnd);
      window.setTimeout(() => {
        body?.removeEventListener("transitionend", onEnd);
        scrollFoldIntoView(section);
      }, REVEAL_MS + 200);
    }, 0);
  };
  // « Continuer » : l'étape ouverte est validée et la suivante s'ouvre ; s'il y manque un préalable, il est désigné.
  const openStep = open !== null && open !== "options" ? stepOf(open) : null;
  // Volet choisi par l'utilisateur (titre, repère du rail) : l'étape quittée, si rien n'y manque, compte pour validée
  // (ses valeurs par défaut conviennent) ; « Continuer » ne la repropose pas.
  const pickFold = (fold: Fold | null) => {
    if (openStep && !openStep.blocking && !confirmed.has(openStep.key)) setConfirmed(new Set([...confirmed, openStep.key]));
    openFold(fold);
  };
  const toggleFold = (fold: Fold) => pickFold(open === fold ? null : fold);
  const advance = () => {
    if (openStep?.blocking) {
      designateStep(openStep);
      return;
    }
    const done = new Set(confirmed);
    if (openStep) done.add(openStep.key);
    setConfirmed(done);
    openFold(stepAfter(steps, done, open));
  };
  const mode = primaryMode({ saved, open, steps, confirmed });
  // Cible désignée dans un volet replié (accueil, palette, cohérence, calques) : le volet s'ouvre, puis elle est
  // désignée (`designate`, `REVEAL_EVENT`). Réglage drone demandé par une autre vue : les options s'ouvrent.
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const onReveal = (event: Event) => {
      const target = event.target as HTMLElement;
      const fold = target.closest<HTMLElement>("[data-fold]");
      const closed = target.closest<HTMLElement>('[data-open="false"]');
      if (!fold || !closed || !fold.contains(closed)) return;
      event.preventDefault();
      setOpen(fold.dataset.fold as Fold);
    };
    const onDrones = () => setOpen("options");
    form.addEventListener(REVEAL_EVENT, onReveal);
    window.addEventListener(OPEN_DRONES_SETTINGS, onDrones);
    return () => {
      form.removeEventListener(REVEAL_EVENT, onReveal);
      window.removeEventListener(OPEN_DRONES_SETTINGS, onDrones);
    };
  }, []);

  // Bouton principal : il dit ce qui suit (`saveKind`). « Placer le terrain » n'écrit rien : la vue Manœuvre s'ouvre
  // à l'étape Terrain, le dossier garde sa carte (ou aucune) jusqu'au nouveau terrain.
  const submit = () => {
    if (lockedDossier) {
      designateDossierReadOnly();
      return;
    }
    if (missing[0]) {
      designateStep(missing[0]);
      return;
    }
    if (kind === "place") {
      removeStoredDraft(draftKey);
      adopt(freshDraftState(current));
      setSiteChoice(null);
      openTerrainStep();
      return;
    }
    save.mutate(saveIntent());
  };
  // « Annuler les modifications » : le dossier enregistré, son camp compris (effet du camp affiché).
  const resetDraft = () => {
    adopt(freshDraftState(current));
    preferredMission.current = null;
    setSiteChoice(null);
    // « Effacer » un nouveau dossier : le parcours repart de la première étape.
    if (!current) {
      setConfirmed(new Set());
      openFold("dossier");
    }
  };

  const forceGroups = useMemo(
    () => groupBy<Force>(forces, (f) => (f.user ? (f.manoeuvre === false ? USER_SUPPORT_GROUP : USER_GROUP) : (f.group ?? ""))),
    [forces],
  );
  const hasUserForces = forces.some((f) => f.user);
  // Posture (demande d'Alexis du 20/10/2026, docs/25 §12) : postures que la doctrine prévoit au niveau, liste
  // « Mission » filtrée sur la posture choisie.
  const postures = useMemo(() => availablePostures(missions), [missions]);
  const posture = draftPosture(draft, missions);
  const shownMissions = useMemo(() => missionsFor(missions, posture), [missions, posture]);
  const missionGroups = useMemo(() => groupBy<MissionOption>(missions, (m) => m.mode), [missions]);
  const pickPosture = (next: DossierPosture) => {
    const { draft: chosen, replaced } = choosePosture(draft, next, missions);
    setDraft(() => chosen);
    if (replaced) {
      // Mission d'une autre posture : remplacée par la mission par défaut de la posture, avec un avis ; la liste
      // « Mission » est désignée (les autres missions de la posture y sont proposées).
      notify({
        text: t("Mission « {mission} » choisie", { mission: missionName(replaced.to) }),
        detail: t("« {before} » n'est pas une mission de cette posture. Les autres missions de la posture sont dans la liste « Mission ».", {
          before: missionName(replaced.from),
        }),
        tone: "info",
      });
      designateSoon(`${attentionSelector("dossier-mission")} [role="combobox"]`, "field", 350);
    }
  };

  // Phrase « Ensuite » du pied, sur un dossier enregistré : une étape attendue autre que celle ouverte ; jamais en
  // lecture seule. Nouveau dossier : « Continuer » mène d'une étape à l'autre, une seule chose à la fois.
  const showNext = !lockedDossier && saved && nextStep !== null && nextStep.key !== open;
  const nextId = `${formId}-next`;

  // Pastille d'une étape dans l'accordéon : anneau accent pour l'étape ouverte, numéro seul pour une étape pas encore
  // atteinte (nouveau dossier), sinon l'état balisé (coche, à remplir, à revoir, en attente).
  const stepMark = (step: Step) => (
    <StepMark
      state={open === step.key ? "next" : reached(step.key) ? stepMarkState(step, nextStep) : "pending"}
      n={step.n}
      total={steps.length}
      status={stepBadge(step, nextStep)?.text}
    />
  );
  // Résumés des volets repliés : une ligne lisible, les choix faits séparés par « · ».
  const levelCard = LEVELS.find((lvl) => lvl.value === level);
  const levelLabel = levelCard ? t(level === "COMPAGNIE" ? companyCard(chosenForce).label : levelCard.label) : null;
  const placeName =
    layout === "centac"
      ? (maps.find((m) => m.slug === CENTAC_MAP)?.name ?? null)
      : newTerrain
        ? summaryLine([t("Nouveau terrain"), draftPlace?.name])
        : (maps.find((m) => m.slug === draft.mapSlug)?.name ?? zones.find((z) => z.slug === draft.mapSlug)?.name ?? null);
  const enemyName = isCentac
    ? "Titan"
    : neutral
      ? genericEnemyLabel()
      : chosenCustomSummary
        ? t("ENI {code}", { code: chosenCustomSummary.code })
        : (draft.parameters.enemy_type ?? null);
  const robots = [draft.parameters.robotized_ami && t("Unité amie robotisée"), draft.parameters.robotized_eni && t("Ennemi robotisé")];
  const populationLabel = POPULATIONS.find((p) => p.value === (draft.parameters.population ?? "Amicale"))?.label ?? POPULATIONS[0].label;
  const summaries: Record<Fold, string> = {
    dossier: summaryLine([draft.name.trim(), levelLabel]),
    force: summaryLine([
      chosenForce ? unitDesignation(chosenForce.label) : null,
      isCentac
        ? t("Axe {cap}°", { cap: draft.parameters.centac_rotation_deg ?? 0 })
        : posture
          ? t(POSTURE_LABELS[posture])
          : null,
      !isCentac && draft.parameters.mission ? missionName(draft.parameters.mission) : null,
    ]),
    lieu: placeName ?? "",
    date: summaryLine([
      draft.parameters.start_dtg ? formatDtg(draft.parameters.start_dtg, 0, draft.parameters.time_zone ?? null) : t("Début à la création du dossier"),
      isCentac ? t("Météo de saison") : weatherState(draft.parameters) === "custom" ? t("Météo choisie") : t("Météo saisonnière"),
    ]),
    ennemi: summaryLine([enemyName, eniChosen ? unitDesignation(eniChosen.name) : null]),
    options: summaryLine([
      !isCentac && level !== "CORPS" && (hasChosen(draft.parameters.drones) ? t("Drones réglés") : t("Drones : valeurs doctrinales")),
      // « Population amicale » ; en anglais, « Friendly population ».
      t("Population {attitude}", { attitude: lang() === "fr" ? t(populationLabel).toLocaleLowerCase("fr") : t(populationLabel) }),
      draft.parameters.strategic_site && t("Sites stratégiques"),
      draft.parameters.militia && t("Milices"),
      robots.some(Boolean) ? summaryLine(robots) : t("Aucune robotisation"),
      !isCentac && draft.parameters.enemy_profile && t("Profil du chef ENI : {profil}", { profil: profileName(draft.parameters.enemy_profile) }),
    ]),
  };

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.mission.icon}
      title={current ? t("Données initiales") : t("Nouveau dossier d'exercice")}
      actions={
        <>
          {current && (
            <>
              <IconLink
                size="sm"
                href={api.exerciseDownloadUrl(current.id)}
                // Licence d'essai (registre F44) : grisé, l'appui le dit (avis), le survol aussi.
                blocked={trialExport.blocked}
                onBlocked={trialExport.onBlocked}
                title={trialExport.title ?? t("Télécharger le dossier sur cet ordinateur (fichier .generex : paramètres, dispositif, ordres)")}
                aria-label={t("Télécharger le dossier")}
              >
                <Download size={16} />
              </IconLink>
              <IconButton size="sm" title={t("Supprimer le dossier")} aria-label={t("Supprimer le dossier")} onClick={() => setConfirmDelete(true)}>
                <Trash2 size={16} />
              </IconButton>
            </>
          )}
          <HelpButton screen="donnees" buttonSize="sm" size={16} />
        </>
      }
      footer={
        // Barre d'actions hors de la zone qui défile : « Enregistrer » reste visible, ce qu'il effacerait et un
        // échec d'enregistrement s'affichent au-dessus, près de lui.
        <div ref={saveBarRef} className="flex min-w-0 flex-1 flex-col gap-2.5">
          <Presence show={losses.length > 0}>
            {losses.length > 0 && (
              <Callout tone="warn">
                <ul className="space-y-0.5">
                  {losses.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                </ul>
              </Callout>
            )}
          </Presence>
          <Presence show={Boolean(error)}>{error && <Callout tone="danger" report={!inputError}>{error}</Callout>}</Presence>
          {/* Ce qui est attendu ailleurs que dans l'étape ouverte (désigné d'un appui) : l'étape ouverte, elle, montre
              déjà ses champs à remplir. Jamais en lecture seule. */}
          <Presence show={showNext}>
            {showNext && nextStep && (
              <Motion key={`${nextStep.key}-${nextStep.state}`} origin="fade">
                <Button id={nextId} variant="link" className="text-left" onClick={() => designateStep(nextStep, true)}>
                  {nextLine(nextStep)}
                </Button>
              </Motion>
            )}
          </Presence>
          {/* Le bouton passe sous l'état, aligné à droite, quand son libellé ne tient pas à côté : l'état garde
              112 px (« Brouillon », « Version n » : « Créer et placer le terrain » tient à côté), 176 px quand il dit
              « Modifications non enregistrées », de quoi le tenir sur une ligne au lieu de l'écraser sur trois
              (« Enregistrer et placer le terrain », iPad portrait). Seul le choix « Terrain libre » diffère : rien à
              enregistrer (« Placer le terrain » n'écrit rien), l'état reste « Version n ». */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <span
              className={clsx(
                "flex flex-1 flex-wrap items-center gap-x-2 text-meta text-ink-muted",
                current && dirtyWithoutChoice ? "min-w-44" : "min-w-28",
              )}
            >
              {current
                ? dirtyWithoutChoice
                  ? t("Modifications non enregistrées")
                  : t("Version {version}", { version: current.version })
                : t("Brouillon")}
              <Presence show={unsaved}>
                <Motion origin="fade" className="inline-flex">
                  <Button variant="link" onClick={resetDraft}>
                    {current ? t("Annuler les modifications") : t("Effacer")}
                  </Button>
                </Motion>
              </Presence>
            </span>
            {mode === "continue" ? (
              // Nouveau dossier : « Continuer » valide l'étape ouverte et ouvre la suivante (le champ manquant est
              // désigné) ; à la dernière étape, le bouton d'enregistrement prend sa place.
              <Button
                type="submit"
                form={formId}
                className="ml-auto"
                blocked={lockedDossier || Boolean(openStep?.blocking)}
                onBlocked={() => (lockedDossier ? designateDossierReadOnly() : openStep && designateStep(openStep))}
                title={openStep?.blocking ? missingLine([openStep]) : undefined}
              >
                {t("Continuer")} <ArrowRight size={14} />
              </Button>
            ) : (
            <Button
              ref={saveButtonRef}
              type="submit"
              form={formId}
              className="ml-auto"
              data-attention={current ? undefined : "dossier-create"}
              disabled={save.isPending || (Boolean(current) && !dirty && kind !== "place" && missing.length === 0)}
              blocked={lockedDossier || missing.length > 0}
              onBlocked={() => (lockedDossier ? designateDossierReadOnly() : missing[0] && designateStep(missing[0]))}
              highlight={(awaitingSave && missing.length === 0) || (kind === "place" && !dirtyWithoutChoice && (savedNoMap || pendingFree || Boolean(draftPlace)))}
              title={
                !lockedDossier && missing.length > 0
                  ? missingLine(missing)
                  : awaitingSave
                    ? t("Enregistrez le dossier pour déplacer les unités et les lignes")
                    : undefined
              }
              aria-describedby={showNext ? nextId : undefined}
            >
              {placesTerrain(kind) ? <Route size={14} /> : <Check size={14} />} {t(SAVE_LABEL[kind])}
            </Button>
            )}
          </div>
        </div>
      }
    >
      <form
        id={formId}
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          if (mode === "continue") advance();
          else submit();
        }}
      >
        {/* Avis sur le dossier lui-même, au-dessus des rubriques. */}
        <div className="space-y-2 px-4 pt-2 empty:hidden">
          {/* Saisie reportée sur une version plus récente du dossier : rien n'est perdu, l'utilisateur vérifie. */}
          <Presence show={rebased && unsaved}>
            {rebased && unsaved && (
              <Callout tone="info">
                {t("Le dossier a changé depuis votre saisie : vos modifications sont gardées, vérifiez-les puis enregistrez.")}
                {conflicted.size > 0 && (
                  <span className="mt-1 block">{t("Les champs modifiés aussi dans le dossier enregistré sont signalés : votre valeur y est gardée.")}</span>
                )}
              </Callout>
            )}
          </Presence>
          {/* Dossier transmis : l'ordre de son chef a changé depuis la transmission (ordres transmis prévenus). */}
          <ChiefOrderCallout exercise={current} />

        </div>
        {/* Parcours guidé (refonte du 02/11/2026) : le rail des cinq étapes, puis un accordéon — Dossier, Force amie,
            Lieu, Date et météo, Ennemi, dans l'ordre de la réflexion du chef — et les options facultatives. Un seul
            volet ouvert ; replié, chacun se résume en une ligne. */}
        <StepsRail
          steps={steps}
          state={(step) => railState(step, { open, confirmed, saved })}
          label={stepsProgressLabel(stepped)}
          onPick={(step) => pickFold(step.key)}
        />
        <FoldSection
          fold="dossier"
          open={open === "dossier"}
          mark={stepMark(stepOf("dossier"))}
          title={t(stepOf("dossier").title)}
          summary={reached("dossier") ? summaries.dossier : undefined}
          badge={reached("dossier") ? stepBadge(stepOf("dossier"), nextStep) : null}
          editable={saved || confirmed.has("dossier")}
          onToggle={() => toggleFold("dossier")}
        >
          {isCentac ? (
            // CENTAC (demandes d'Alexis du 26/10/2026, registres A119 et A122) : un seul champ, « Rotation NN/AA », qui
            // nomme le dossier et sa mission ; aucun nom à saisir en plus. Le gabarit ROT08 n'est que le modèle de
            // déroulement des quatre OPO.
            <Motion origin="fade" key="centac-name">
              <CentacRotationField
                value={draft.parameters.centac_rotation}
                onChange={setRotation}
                touched={rotationTouched}
                onTouched={setRotationTouched}
                inputRef={nameRef}
                awaiting={awaitingOf("dossier-centac-rotation")}
                note={rotationNote}
                conflict={<ConflictHint show={conflictOn("parameters.centac_rotation")} onTake={() => takeServer("parameters.centac_rotation")} />}
              />
            </Motion>
          ) : (
            <Motion origin="fade" key="free-name">
              <Field label={t("Nom du dossier")}>
                <input
                  ref={nameRef}
                  data-attention="dossier-name"
                  data-awaiting={awaitingOf("dossier-name")}
                  className={inputClass}
                  value={draft.name}
                  placeholder={t("ex. Exercice Brennus 26")}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                />
              </Field>
              <ConflictHint show={conflictOn("name")} onTake={() => takeServer("name")} />
            </Motion>
          )}

          <div data-attention="dossier-level" className="rounded-lg">
            <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Niveau de l'ordre")}</span>
            <div className="grid grid-cols-2 gap-1.5" role="group" aria-label={t("Niveau de l'ordre")}>
              {LEVELS.map((lvl, i) => {
                const card = lvl.value === "COMPAGNIE" && level === "COMPAGNIE" ? companyCard(chosenForce) : lvl;
                return (
                  <ChoiceCard
                    key={lvl.value}
                    selected={level === lvl.value}
                    onClick={() => chooseLevel(lvl.value)}
                    title={t(card.label)}
                    detail={t(card.hint)}
                    // Nombre impair de niveaux : la dernière carte prend toute la ligne.
                    className={clsx("h-full", LEVELS.length % 2 === 1 && i === LEVELS.length - 1 && "col-span-2")}
                  />
                );
              })}
            </div>
            <ConflictHint show={conflictOn("parameters.level")} onTake={() => takeServer("parameters.level")} />
          </div>
        </FoldSection>

        <FoldSection
          fold="force"
          open={open === "force"}
          mark={stepMark(stepOf("force"))}
          title={t(stepOf("force").title)}
          summary={reached("force") ? summaries.force : undefined}
          badge={reached("force") ? stepBadge(stepOf("force"), nextStep) : null}
          editable={saved || confirmed.has("force")}
          onToggle={() => toggleFold("force")}
        >
          <div data-attention="dossier-odb" className="rounded-lg">
            <Field
              label={t("{force} (ODB)", { force: t(FORCE_LABEL[level]) })}
              hint={unknownForce ? t("Cet ordre de bataille n'existe plus : choisissez-en un autre.") : undefined}
            >
              <Select
                className={inputClass}
                awaiting={awaitingOf("dossier-odb")}
                value={draft.parameters.odb ?? ""}
                onChange={(e) => {
                  const odb = e.target.value;
                  // Autre force : sa mission est résolue de nouveau (la même si elle la propose, sinon la première).
                  if (!isCentac) preferredMission.current = draft.parameters.mission;
                  setDraft((d) => ({
                    ...d,
                    parameters: isCentac ? { ...d.parameters, odb } : { ...d.parameters, odb, mission: null, mode_action: null },
                  }));
                }}
              >
                {unknownForce && draft.parameters.odb && (
                  <option value={draft.parameters.odb}>{t("{code} (introuvable)", { code: draft.parameters.odb })}</option>
                )}
                {forceGroups.map(([group, items]) =>
                  group === USER_GROUP || group === USER_SUPPORT_GROUP ? (
                    // Forces construites ; les bataillons d'appui et de soutien (hors manœuvre) à part, nommés comme tels.
                    <optgroup key={group} label={group === USER_GROUP ? t("Vos ordres de bataille") : t("Vos ordres de bataille — appuis et soutien (hors manœuvre)")}>
                      {items.map((f) => {
                        // Force construite avec les symboles APP-6 : sa fonction (palette) plutôt que la spécialité.
                        const fn = f.app6 || f.function ? functionLabel(app6Palette, f.function, f.app6) : null;
                        return (
                          <option key={f.ref} value={f.ref}>
                            {f.group ? `${unitDesignation(f.label)} (${unitDesignation(f.group)})` : unitDesignation(f.label)}
                            {(level === "REGIMENT" || level === "COMPAGNIE") && (fn ?? f.specialty) ? ` — ${fn ?? specialtyLabel(f.specialty)}` : ""}
                          </option>
                        );
                      })}
                    </optgroup>
                  ) : group ? (
                    <optgroup key={group} label={level === "COMPAGNIE" ? companyGroupLabel(group) : t("Brigade {group}", { group })}>
                      {items.map((f) => (
                        <option key={f.ref} value={f.ref}>
                          {unitDesignation(f.label)} — {specialtyLabel(f.specialty)}
                        </option>
                      ))}
                    </optgroup>
                  ) : hasUserForces ? (
                    // Forces du catalogue sous leur propre titre : sans lui, elles sembleraient suivre « Vos ordres de bataille ».
                    <optgroup key="catalogue" label={t("Fournis avec Generex (armée de Terre française)")}>
                      {items.map((f) => (
                        <option key={f.ref} value={f.ref}>
                          {unitDesignation(f.label)}
                        </option>
                      ))}
                    </optgroup>
                  ) : (
                    items.map((f) => (
                      <option key={f.ref} value={f.ref}>
                        {unitDesignation(f.label)}
                      </option>
                    ))
                  ),
                )}
              </Select>
            </Field>
            <ConflictHint show={conflictOn("parameters.odb")} onTake={() => takeServer("parameters.odb")} />
          </div>
          {!isCentac && (
            <div className="-mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  openEniOrbats("AMI", null, { create: { side: "AMI", kind: AMI_KIND_BY_LEVEL[level as Exclude<Level, "CENTAC">] }, origin: orbatsOrigin("odb") });
                  setView("orbats");
                }}
              >
                <Network size={14} /> {t("Créer un ordre de bataille")}
              </Button>
              {chosenForce?.user && !chosenForce.from_exercise && (
                <Button
                  variant="link"
                  onClick={() => {
                    openEniOrbats("AMI", chosenForce.ref.split("/")[0] ?? null, { origin: orbatsOrigin("odb") });
                    setView("orbats");
                  }}
                >
                  <Pencil size={14} /> {t("Modifier « {name} »", { name: unitDesignation(chosenForce.odb_label ?? chosenForce.group ?? chosenForce.label) })}
                </Button>
              )}
            </div>
          )}
          {/* Unités amies ajoutées, retirées, rôles choisis pour ce dossier (docs/18 §6.6). */}
          {!isCentac && <CompositionSummary side="AMI" />}

          {isCentac ? (
            <>
              <div data-attention="dossier-centac-axis" className="rounded-lg">
                <Field
                  label={t("Axe de manœuvre")}
                  hint={t("Direction de l'ennemi depuis la base de départ. Le changer efface les déplacements.")}
                >
                  <Select
                    className={inputClass}
                    value={String(draft.parameters.centac_rotation_deg ?? 0)}
                    onChange={(e) => setParam("centac_rotation_deg", Number(e.target.value) || null)}
                  >
                    {AXES.map((a) => (
                      <option key={a.deg} value={a.deg}>
                        {a.deg}° — {t(a.label)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <ConflictHint
                  show={conflictOn("parameters.centac_rotation_deg")}
                  onTake={() => takeServer("parameters.centac_rotation_deg")}
                />
              </div>
            </>
          ) : (
            <>
              {/* Posture (demande d'Alexis du 20/10/2026) : juste au-dessus de « Mission », segmentée comme « Langue de
                  l'ordre » ; seules les postures prévues au niveau sont proposées. */}
              <Presence show={postures.length > 0 && posture !== null}>
                <Motion origin="fade" data-attention="dossier-posture" className="rounded-lg">
                  <Segmented<DossierPosture>
                    label={t("Posture")}
                    options={postures.map((p) => ({ value: p, label: t(POSTURE_LABELS[p]) }))}
                    value={posture ?? "offensive"}
                    onChange={pickPosture}
                  />
                </Motion>
              </Presence>
              <div data-attention="dossier-mission" className="rounded-lg">
                <Field
                  label={t("Mission")}
                  hint={unknownMission ? t("Cette mission n'existe plus pour cette force : choisissez-en une autre.") : undefined}
                >
                  <Select
                    className={inputClass}
                    awaiting={awaitingOf("dossier-mission")}
                    value={draft.parameters.mission ?? ""}
                    onChange={(e) => {
                      const mission = missions.find((m) => m.name === e.target.value);
                      setDraft((d) => chooseMission(d, mission));
                    }}
                  >
                    {unknownMission && draft.parameters.mission && (
                      <option value={draft.parameters.mission}>{t("{code} (introuvable)", { code: draft.parameters.mission })}</option>
                    )}
                    {posture
                      ? shownMissions.map((m) => (
                          <option key={m.name} value={m.name}>
                            {missionName(m.name)}
                          </option>
                        ))
                      : missionGroups.map(([mode, items]) => (
                          <optgroup key={mode} label={term(mode)}>
                            {items.map((m) => (
                              <option key={m.name} value={m.name}>
                                {missionName(m.name)}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                  </Select>
                </Field>
                <ConflictHint
                  show={conflictOn("parameters.mission", "parameters.mode_action", "parameters.posture")}
                  onTake={() => takeServer("parameters.mission")}
                />
              </div>
            </>
          )}
        </FoldSection>

        <FoldSection
          fold="lieu"
          open={open === "lieu"}
          mark={stepMark(stepOf("lieu"))}
          title={t(stepOf("lieu").title)}
          summary={reached("lieu") ? summaries.lieu : undefined}
          badge={reached("lieu") ? stepBadge(stepOf("lieu"), nextStep) : null}
          editable={saved || confirmed.has("lieu")}
          onToggle={() => toggleFold("lieu")}
        >
          {/* Lieu de l'exercice (demande d'Alexis du 03/10/2026) : « Camp de manœuvre » ou « Terrain libre », puis le
              champ qui en dépend. Cible « dossier-camp » du lien « Ouvrir les Données initiales » (`missionDataTarget`) :
              posée sur le conteneur, faute de transmission de `data-attention` par `Select` ; c'est la liste sous
              `[data-designate]` qui est désignée. */}
          <div ref={choiceRef} data-attention="dossier-camp" className="rounded-lg">
            <Motion key={layout} origin="fade">
              {layout === "centac" ? (
                <Field label={t("Espace d'entraînement")} hint={t("Imposé par la rotation CENTAC.")}>
                  <div className="flex items-center gap-2">
                    <input className={inputClass} value={maps.find((m) => m.slug === CENTAC_MAP)?.name ?? ""} readOnly />
                    <CampOutline slug={CENTAC_MAP} size={32} />
                  </div>
                </Field>
              ) : layout === "cards" ? (
                <>
                  <div
                    role="radiogroup"
                    aria-label={t("Lieu de l'exercice")}
                    className={clsx("grid gap-1.5", sites.length > 1 ? "grid-cols-2" : "grid-cols-1")}
                    onKeyDown={(e) => onRadioGroupKeyDown(e, (i) => pick(sites[i]!))}
                  >
                    {sites.map((s, i) => (
                      <ChoiceCard
                        key={s}
                        radio
                        className="h-full"
                        selected={site === s}
                        tabIndex={rovingTabIndex(i, sites.indexOf(site), sites.map(() => true))}
                        onClick={() => pick(s)}
                        title={s === "camp" ? t("Espace d'entraînement") : t("Terrain libre")}
                        detail={s === "camp" ? t("Prêt à l'emploi, en France.") : t("N'importe où, prêt en quelques minutes.")}
                      />
                    ))}
                  </div>
                  <Presence show={corpsLevel}>
                    <Motion origin="fade" className="mt-1.5">
                      <p className="text-meta leading-snug text-ink-muted">{t(CORPS_FREE_ONLY)}</p>
                    </Motion>
                  </Presence>
                  <Motion key={site === "camp" ? "camp" : zones.length > 0 ? "free-field" : "free-place"} origin="fade" className="mt-2.5 space-y-2.5">
                    {site === "camp" ? (
                      <div data-designate="">
                        <Field label={t("Espace d'entraînement")}>
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <Select className={inputClass} awaiting={awaitingOf("dossier-camp")} value={campValue} onChange={(e) => chooseCamp(e.target.value)}>
                                <option value="">{t("— Choisir un espace d'entraînement —")}</option>
                                {camps.map((m) => (
                                  <option key={m.slug} value={m.slug}>
                                    {m.name}
                                  </option>
                                ))}
                              </Select>
                            </div>
                            <CampOutline slug={campValue || null} size={32} />
                          </div>
                        </Field>
                      </div>
                    ) : zones.length > 0 ? (
                      <div data-designate="">
                        <Field label={t("Terrain")} hint={terrainHint}>
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <Select
                                className={inputClass}
                                awaiting={awaitingOf("dossier-camp")}
                                value={newTerrain ? FREE_TERRAIN : (draft.mapSlug ?? "")}
                                onChange={(e) => chooseTerrain(e.target.value)}
                              >
                                <option value={FREE_TERRAIN}>{t("Nouveau terrain")}</option>
                                {zoneOptionGroup(zones, missingTerrain)}
                              </Select>
                            </div>
                            <CampOutline slug={newTerrain ? null : draft.mapSlug} size={32} />
                          </div>
                        </Field>
                      </div>
                    ) : null}
                    {/* Nouveau terrain : son lieu, nommé ici (ville, coordonnées ou MGRS) ; il situe le climat de la météo et
                        la vue Manœuvre s'y centrera. Hors du bloc `data-designate` (la liste du lieu reste la cible du
                        choix d'un camp ou d'un terrain). */}
                    <Presence show={site === "free" && newTerrain}>
                      {site === "free" && newTerrain && (
                        <Motion origin="fade" className="space-y-1.5">
                          <PlaceField
                            key={draftOwner}
                            value={draftPlace}
                            onChange={choosePlace}
                            terrains={zones}
                            camps={camps}
                            onTerrain={chooseTerrain}
                            onCamp={(slug) => {
                              setSiteChoice("camp");
                              chooseCamp(slug);
                            }}
                            country={neutral ? undefined : "FR"}
                            weather={{
                              startDtg: draft.parameters.start_dtg,
                              timeZone: draft.parameters.time_zone,
                              scenario: draft.parameters.weather_scenario,
                              custom: draft.parameters.weather_custom,
                            }}
                            readOnly={lockedDossier ? designateDossierReadOnly : undefined}
                            awaiting={awaitingOf("dossier-place")}
                            conflict={<ConflictHint show={conflictOn("parameters.place")} onTake={() => takeServer("parameters.place")} />}
                          />
                          {zones.length === 0 && newTerrainHint && (
                            <p className="px-1 text-meta leading-snug text-ink-muted">{newTerrainHint}</p>
                          )}
                        </Motion>
                      )}
                    </Presence>
                  </Motion>
                </>
              ) : (
                // Terrain libre non proposé (serveur fermé, carte mondiale absente, état de licence pas encore connu) :
                // une seule liste, sans « Nouveau terrain » ni mention du terrain libre.
                <div data-designate="">
                  <Field label={zones.length > 0 ? t("Lieu de l'exercice") : t("Espace d'entraînement")} hint={zones.length > 0 ? terrainHint : undefined}>
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <Select
                          className={inputClass}
                          awaiting={awaitingOf("dossier-camp")}
                          value={draft.mapSlug ?? ""}
                          onChange={(e) => chooseMap(e.target.value || null)}
                        >
                          <option value="">{zones.length > 0 ? t("— Choisir un espace d'entraînement ou un terrain —") : t("— Choisir un espace d'entraînement —")}</option>
                          {zones.length > 0 ? (
                            <optgroup label={t("Camps")}>
                              {camps.map((m) => (
                                <option key={m.slug} value={m.slug}>
                                  {m.name}
                                </option>
                              ))}
                            </optgroup>
                          ) : (
                            camps.map((m) => (
                              <option key={m.slug} value={m.slug}>
                                {m.name}
                              </option>
                            ))
                          )}
                          {zoneOptionGroup(zones, missingTerrain)}
                        </Select>
                      </div>
                      <CampOutline slug={draft.mapSlug} size={32} />
                    </div>
                  </Field>
                  <Presence show={unavailableNote !== null}>
                    {unavailableNote && (
                      <Motion origin="fade" className="mt-2.5">
                        <Callout tone="info">{unavailableNote}</Callout>
                      </Motion>
                    )}
                  </Presence>
                  <Presence show={corpsLevel}>
                    <Motion origin="fade" className="mt-2.5">
                      <Callout tone="info">{t(CORPS_FREE_ONLY)}</Callout>
                    </Motion>
                  </Presence>
                </div>
              )}
            </Motion>
            <ConflictHint show={conflictOn("mapSlug", "freeTerrain")} onTake={() => takeServer("mapSlug")} />
          </div>
          {/* Vue Manœuvre toujours accessible pour une carte posée : retoucher le fuseau et les LIMA d'un camp ou d'un
              terrain de la licence. */}
          <Presence show={showManoeuvreLink}>
            <Motion origin="fade" className="-mt-1.5">
              <Button variant="link" onClick={() => setView("manoeuvre")}>
                <Route size={14} /> {t("Construire la manœuvre")}
              </Button>
            </Motion>
          </Presence>

          {/* Profil du lieu choisi, celui du paragraphe Terrain de l'ordre (l'analyse OAKOC des cartes de l'iPad donnait
              la même mobilité « Favorable » et la même conclusion à tous les camps), sous le choix du lieu. Masqué pour
              « Nouveau terrain » : il ne présente pas comme nouveau lieu la carte que le terrain remplacera. */}
          <Presence show={Boolean(mapDetail?.profile) && showMapProfile}>
            {mapDetail?.profile && (
              <Motion key={mapDetail.slug} origin="fade">
                <dl className="space-y-1 rounded-lg border border-line bg-canvas px-3 py-2.5 text-meta leading-relaxed text-ink-soft">
                  {(
                    [
                      [t("Relief"), mapDetail.profile.relief],
                      [t("Végétation"), mapDetail.profile.vegetation],
                      [t("Coupures"), mapDetail.profile.water],
                      [t("Grands axes"), mapDetail.profile.roads],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label}>
                      <dt className="inline font-medium text-ink">{t("{label} :", { label })} </dt>
                      <dd className="inline">{value.charAt(0).toUpperCase() + value.slice(1)}.</dd>
                    </div>
                  ))}
                </dl>
              </Motion>
            )}
          </Presence>
        </FoldSection>

        <FoldSection
          fold="date"
          open={open === "date"}
          mark={stepMark(stepOf("date"))}
          title={t(stepOf("date").title)}
          summary={reached("date") ? summaries.date : undefined}
          badge={reached("date") ? stepBadge(stepOf("date"), nextStep) : null}
          editable={saved || confirmed.has("date")}
          onToggle={() => toggleFold("date")}
        >
          <div>
            <Field
              label={t("Début de l'exercice (GDH)")}
              hint={draft.parameters.start_dtg ? undefined : t("Vide : l'heure de création du dossier.")}
            >
              <input
                type="datetime-local"
                className={inputClass}
                value={draft.parameters.start_dtg ? draft.parameters.start_dtg.slice(0, 16) : ""}
                onChange={(e) => setParam("start_dtg", e.target.value ? `${e.target.value}:00` : null)}
              />
            </Field>
            <ConflictHint show={conflictOn("parameters.start_dtg")} onTake={() => takeServer("parameters.start_dtg")} />
          </div>

          <div>
            <Field label={t("Fuseau horaire")}>
              <Select
                className={inputClass}
                value={draft.parameters.time_zone ?? "Z"}
                onChange={(e) => {
                  if (isZoneLetter(e.target.value)) setParam("time_zone", e.target.value);
                }}
              >
                {ZONES_WEST_TO_EAST.map((letter) => (
                  <option key={letter} value={letter}>
                    {/* Lettre proposée par la zone libre du dossier (heure légale au début de l'exercice) : signalée. */}
                    {letter === zoneLetter
                      ? placeLetters
                        ? t("{fuseau} — heure légale du lieu", { fuseau: zoneLabel(letter) })
                        : t("{fuseau} — heure légale du terrain", { fuseau: zoneLabel(letter) })
                      : zoneLabel(letter)}
                  </option>
                ))}
              </Select>
            </Field>
            <ConflictHint show={conflictOn("parameters.time_zone")} onTake={() => takeServer("parameters.time_zone")} />
          </div>
          <Presence show={offerZoneLetter}>
            {zoneLetter && (
              <Motion origin="fade" className="-mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-meta text-ink-muted">
                <span>
                  {placeLetters
                    ? t("Heure légale du lieu : {zone}", { zone: zoneLabel(zoneLetter) })
                    : t("Heure légale du terrain : {zone}", { zone: zoneLabel(zoneLetter) })}
                </span>
                <Button variant="link" onClick={() => setParam("time_zone", zoneLetter)}>
                  {t("Appliquer")}
                </Button>
              </Motion>
            )}
          </Presence>

          {/* Météo (saisonnière ou choisie) : aucun réglage en CENTAC, la météo de saison du camp se retouche dans
              l'ordre de la rotation. Le brouillon se réajuste après un changement de lieu, de date ou de fuseau fait
              ici, jamais à l'ouverture ; remonté à chaque dossier. */}
          {!isCentac && (
            <WeatherField
              key={draftOwner}
              mapSlug={newTerrain ? null : draft.mapSlug}
              place={draftPlace}
              onGap={setWeatherGap}
              startDtg={draft.parameters.start_dtg}
              timeZone={draft.parameters.time_zone}
              scenario={draft.parameters.weather_scenario}
              custom={draft.parameters.weather_custom}
              onChange={setWeather}
              pending={weatherPending}
              adjust
              readOnly={lockedDossier ? designateDossierReadOnly : undefined}
              conflict={
                <ConflictHint
                  show={conflictOn("parameters.weather_scenario", "parameters.weather_custom")}
                  onTake={() => {
                    takeServer("parameters.weather_scenario");
                    takeServer("parameters.weather_custom");
                  }}
                />
              }
            />
          )}
        </FoldSection>

        <FoldSection
          fold="ennemi"
          open={open === "ennemi"}
          mark={stepMark(stepOf("ennemi"))}
          title={t(stepOf("ennemi").title)}
          summary={reached("ennemi") ? summaries.ennemi : undefined}
          badge={reached("ennemi") ? stepBadge(stepOf("ennemi"), nextStep) : null}
          editable={saved || confirmed.has("ennemi")}
          onToggle={() => toggleFold("ennemi")}
        >
          {/* Force non française : l'ennemi générique, champ fixe (registre B39) ; bascule en fondu quand la force
              change dans le brouillon. */}
          <Motion key={neutral ? "generic" : "reference"} origin="fade" data-attention="dossier-enemy" className="rounded-lg">
            {neutral ? (
              <Field
                label={t("Type d'ennemi")}
                hint={t("Une force construite affronte l'ennemi générique ; détaillez-le si besoin avec un ordre de bataille ennemi générique, ci-dessous.")}
              >
                <input className={inputClass} value={genericEnemyLabel()} disabled readOnly />
              </Field>
            ) : (
              <>
                <Field
                  label={t("Type d'ennemi")}
                  hint={
                    isCentac || !eniOdb
                      ? undefined
                      : eniFaction
                        ? t("Faction de l'ordre de bataille ennemi.")
                        : t("Ennemi de référence des emplacements que votre ordre de bataille ennemi ne pourvoit pas.")
                  }
                >
                  <Select
                    className={inputClass}
                    value={chosenCustom ? customValue(chosenCustom) : (draft.parameters.enemy_type ?? "")}
                    disabled={isCentac || (eniFaction && customEnemies.length === 0)}
                    onChange={(e) => chooseEnemy(e.target.value)}
                    onAction={() => openCustomEnemies(null, true)}
                  >
                    {ENEMIES.map((en) => (
                      // ODB ennemi d'une faction : l'ennemi de référence est sa faction ; un ENI personnalisé reste
                      // possible (sa doctrine, les forces de l'ODB).
                      <option key={en} value={en} disabled={eniFaction && en !== draft.parameters.enemy_type}>
                        {en}
                      </option>
                    ))}
                    {customEnemies.length > 0 && (
                      <optgroup label={t("ENI personnalisés")}>
                        {customEnemies.map((c) => (
                          <option key={c.id} value={customValue(c.id)} disabled={c.status !== "ready"}>
                            {c.status === "ready" ? t("ENI {code}", { code: c.code }) : t("ENI {code} (brouillon)", { code: c.code })}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {!isCentac && (
                      <option value="create-custom-enemy" data-action="">
                        {t("Créer un ENI personnalisé…")}
                      </option>
                    )}
                  </Select>
                </Field>
                {/* Création d'un ENI personnalisé : bouton visible sous le champ (l'option de la liste reste), tant
                    qu'aucun ENI personnalisé n'est choisi ; jamais en CENTAC. */}
                <Presence show={!isCentac && !chosenCustom}>
                  <Motion origin="fade" className="mt-1.5">
                    <Button variant="secondary" size="sm" onClick={() => openCustomEnemies(null, true)}>
                      <Plus size={14} /> {t("Créer un ENI personnalisé…")}
                    </Button>
                  </Motion>
                </Presence>
                {/* ENI personnalisé choisi (registre A135) : l'état de son modèle et « Modifier… », en fondu. */}
                <Presence show={Boolean(chosenCustomSummary)}>
                  {chosenCustomSummary && (
                    <Motion origin="fade" className="mt-1.5 space-y-1">
                      <TrainingState enemy={chosenCustomSummary} />
                      <Button variant="link" onClick={() => openCustomEnemies(chosenCustomSummary.id)}>
                        <Pencil size={14} /> {t("Modifier « ENI {code} »", { code: chosenCustomSummary.code })}
                      </Button>
                    </Motion>
                  )}
                </Presence>
                <Presence show={Boolean(chosenCustom) && !chosenCustomSummary && customListing.isSuccess}>
                  <Motion origin="fade" className="mt-1.5 text-meta leading-snug text-warn">
                    {t("ENI personnalisé introuvable : l'ennemi de référence le remplace.")}
                  </Motion>
                </Presence>
                <ConflictHint
                  show={conflictOn("parameters.enemy_type")}
                  onTake={() => takeServer("parameters.enemy_type")}
                />
              </>
            )}
          </Motion>
          {!isCentac && (
            <EniOdbField
              level={level}
              value={eniOdb}
              choices={eniChoices}
              chosen={eniChosen}
              loaded={Boolean(eniListing)}
              enemy={draft.parameters.enemy_type}
              neutral={neutral}
              legacy={eniLegacy}
              onChange={(code) => {
                const odb = eniChoices.find((o) => o.code === code);
                setDraft((d) => ({
                  ...d,
                  parameters: {
                    ...d.parameters,
                    eni_odb: code,
                    // L'ennemi devient la faction de l'ordre de bataille ennemi (comme le serveur à l'enregistrement) ;
                    // face à l'ennemi générique, il reste vide.
                    enemy_type: neutral ? null : ((odb?.faction_name as EnemyType | undefined) ?? d.parameters.enemy_type),
                  },
                }));
              }}
              onReference={(enemy) =>
                setDraft((d) => ({ ...d, parameters: { ...d.parameters, eni_odb: null, enemy_type: enemy } }))
              }
              onCreate={() => {
                openEniOrbats("ENI", null, {
                  create: { side: "ENI", kind: ENI_KIND_BY_LEVEL[level as Exclude<Level, "CENTAC">], start: "structure" },
                  origin: orbatsOrigin("eni_odb"),
                });
                setView("orbats");
              }}
              onEdit={
                // ODB enregistré dans le dossier, privé à un autre compte (`from_exercise`) : pas d'éditeur.
                eniChosen && !eniChosen.from_exercise
                  ? () => {
                      openEniOrbats("ENI", eniChosen.code, { origin: orbatsOrigin("eni_odb") });
                      setView("orbats");
                    }
                  : null
              }
              conflict={<ConflictHint show={conflictOn("parameters.eni_odb")} onTake={() => takeServer("parameters.eni_odb")} />}
            />
          )}
        </FoldSection>

        {/* Options (facultatif) : ce qui a une valeur par défaut raisonnable et se règle au besoin — drones,
            environnement, robotisation, profil du chef ENI. Replié par défaut, avec le résumé des valeurs retenues. */}
        <FoldSection
          fold="options"
          open={open === "options"}
          title={t("Options (facultatif)")}
          summary={summaries.options}
          onToggle={() => toggleFold("options")}
        >
          {/* Drones (chantier « Drones ») : résumé de ce qui est déduit, réglages fins sous « Régler l'emploi des drones ».
              Ni en CENTAC (rien de nouveau), ni au corps (mesures 3D de ses annexes). */}
          {!isCentac && level !== "CORPS" && (
            <div className="space-y-1.5">
              <OptionHeading actions={<HelpButton screen="donnees" topic="donnees.drones" buttonSize="sm" size={14} />}>{t("Drones")}</OptionHeading>
              <DronesField
                key={draftOwner}
                exerciseId={current?.id ?? null}
                draft={{
                  level,
                  odb: draft.parameters.odb,
                  mission: draft.parameters.mission,
                  mode_action: draft.parameters.mode_action,
                  posture: draft.parameters.posture,
                  enemy_type: neutral ? null : draft.parameters.enemy_type,
                  custom_enemy: chosenCustom,
                  eni_odb: draft.parameters.eni_odb,
                  drones: draft.parameters.drones ?? null,
                }}
                weather={{
                  mapSlug: newTerrain ? null : draft.mapSlug,
                  place: draftPlace,
                  startDtg: draft.parameters.start_dtg,
                  timeZone: draft.parameters.time_zone,
                  scenario: draft.parameters.weather_scenario,
                  custom: draft.parameters.weather_custom,
                  waiting: Boolean(weatherPending),
                }}
                onChange={(drones) => setParam("drones", drones)}
                onEditCustomEnemy={(id) => openCustomEnemies(id)}
                readOnly={lockedDossier ? designateDossierReadOnly : undefined}
                conflict={<ConflictHint show={conflictOn("parameters.drones")} onTake={() => takeServer("parameters.drones")} />}
              />

            </div>
          )}
          <div className="space-y-3">
            <OptionHeading>{t("Environnement")}</OptionHeading>
            <div>
              <Segmented
                label={t("Population")}
                options={translated(POPULATIONS)}
                value={draft.parameters.population ?? "Amicale"}
                onChange={(value) => setParam("population", value)}
              />
              <ConflictHint show={conflictOn("parameters.population")} onTake={() => takeServer("parameters.population")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Segmented
                  label={t("Sites stratégiques")}
                  options={translated(YES_NO)}
                  value={draft.parameters.strategic_site ? "Oui" : "Non"}
                  onChange={(value) => setParam("strategic_site", value === "Oui")}
                />
                <ConflictHint show={conflictOn("parameters.strategic_site")} onTake={() => takeServer("parameters.strategic_site")} />
              </div>
              <div>
                <Segmented
                  label={t("Milices")}
                  options={translated(YES_NO)}
                  value={draft.parameters.militia ? "Oui" : "Non"}
                  onChange={(value) => setParam("militia", value === "Oui")}
                />
                <ConflictHint show={conflictOn("parameters.militia")} onTake={() => takeServer("parameters.militia")} />
              </div>
            </div>

          </div>
          <div className="space-y-2">
            <OptionHeading>{t("Robotisation")}</OptionHeading>
            <div>
              <Checkbox
                checked={draft.parameters.robotized_ami}
                onChange={(checked) => setParam("robotized_ami", checked)}
                label={t("Unité amie robotisée")}
              />
              <ConflictHint show={conflictOn("parameters.robotized_ami")} onTake={() => takeServer("parameters.robotized_ami")} />
            </div>
            <div>
              <Checkbox
                checked={draft.parameters.robotized_eni}
                onChange={(checked) => setParam("robotized_eni", checked)}
                label={t("Ennemi robotisé")}
              />
              <ConflictHint show={conflictOn("parameters.robotized_eni")} onTake={() => takeServer("parameters.robotized_eni")} />
            </div>
          </div>
          {/* Profil du chef ENI (registre D18) : mode facultatif, éteint par défaut ; sans objet en CENTAC (l'ennemi y
              est joué par la FORAD selon le scénario de la rotation). */}
          {!isCentac && (
            <EnemyProfileField
              value={draft.parameters.enemy_profile ?? null}
              onChange={(value) => setParam("enemy_profile", value)}
              conflict={
                <ConflictHint show={conflictOn("parameters.enemy_profile")} onTake={() => takeServer("parameters.enemy_profile")} />
              }
            />
          )}
        </FoldSection>
      </form>

      <Presence show={confirmDelete && Boolean(current)}>
        {confirmDelete && current && (
          <ConfirmDialog
            title={t("Supprimer « {name} » ?", { name: current.name })}
            confirmLabel={t("Supprimer le dossier")}
            busy={remove.isPending}
            onConfirm={() => remove.mutate()}
            onCancel={() => setConfirmDelete(false)}
            extra={
              <ButtonLink variant="link" href={api.exerciseDownloadUrl(current.id)} {...trialExport}>
                <Download size={14} /> {t("Télécharger d'abord")}
              </ButtonLink>
            }
          >
            {/* Liste exacte de ce qui est effacé, et ce que le fichier .generex en garde (audit M11). */}
            <DeletionContents exerciseId={current.id} />
            <Presence show={remove.isError}>
              {remove.isError && (
                <Callout tone="danger" className="mt-2">
                  {remove.error instanceof ApiError && remove.error.status === 412
                    ? t("Ce dossier vient d'être modifié sur un autre poste : vérifiez-le, puis confirmez de nouveau.")
                    : t("La suppression a échoué : réessayez.")}
                </Callout>
              )}
            </Presence>
          </ConfirmDialog>
        )}
      </Presence>
    </FloatingPanel>
  );
}
